/**
 * THE LIVING WAR ROOM
 * Auto-generated, personalized debate between AI gods.
 * The user walks into an active boardroom — gods already arguing.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWorkflow } from '../../contexts/WorkflowContext';
import { RiverButton, RiverBanner } from '../ui/RiverFlow';
import { 
  Send, Loader2, MessageSquare, Users, 
  Zap, RefreshCw, ChevronDown, Flame,
  Bookmark, Share2, Check, Copy,
  Swords, Target, CheckCircle2, AlertTriangle, Shield, Video,
  Volume2, VolumeX, Square, Play, Pause, Mic, MicOff,
  Eye, Crown, SkipForward, ThumbsUp, Link2,
  FileDown, MessageCircle, X, Sparkles, Trophy, ChevronRight, BarChart3
} from 'lucide-react';
import { playGodTheme, stopGodTheme, playGavel, playAlliance, playVerdict } from '../../lib/warRoomAudio';
import { useEssenceReward } from '../../hooks/useEssenceReward';
import { ConsciousnessMilestoneModal } from './ConsciousnessMilestoneModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOD_CONFIG = {
  calliope: {
    name: 'Calliope',
    emoji: '🎭',
    title: 'The Creative Wildcard',
    gradient: 'from-pink-500 to-purple-600',
    text: 'text-pink-400',
    border: 'border-pink-500/30',
    bg: 'bg-pink-500/10',
    glow: 'rgba(236, 72, 153, 0.3)',
    ring: 'ring-pink-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/ce91b6545f15622391c4c2ac9d1f5cd55e84a26127d08498fa328e3448ca768b.png',
  },
  eris: {
    name: 'Eris',
    emoji: '⚔️',
    title: 'The Contrarian',
    gradient: 'from-red-600 to-pink-600',
    text: 'text-red-400',
    border: 'border-red-500/30',
    bg: 'bg-red-500/10',
    glow: 'rgba(239, 68, 68, 0.3)',
    ring: 'ring-red-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/cca42cf3511e6d2f9552066344dce9815a5bdc4d93685a009d4e455a24ef94e4.png',
  },
  plutus: {
    name: 'Plutus',
    emoji: '💰',
    title: 'The CFO',
    gradient: 'from-green-500 to-emerald-600',
    text: 'text-green-400',
    border: 'border-green-500/30',
    bg: 'bg-green-500/10',
    glow: 'rgba(34, 197, 94, 0.3)',
    ring: 'ring-green-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/8cd78bbd81e2f9e93b147efc4d846bf1e630faed368376f1df76837b545431bc.png',
  },
  hestia: {
    name: 'Hestia',
    emoji: '🏠',
    title: 'The Customer Advocate',
    gradient: 'from-orange-400 to-amber-500',
    text: 'text-orange-300',
    border: 'border-orange-400/30',
    bg: 'bg-orange-400/10',
    glow: 'rgba(251, 146, 60, 0.3)',
    ring: 'ring-orange-400/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/8cde7377dac9c09926c85e6194e06e34ff3639a21b77b654aa00ce1593ff64a1.png',
  },
  themis: {
    name: 'Themis',
    emoji: '⚖️',
    title: 'The Data Purist',
    gradient: 'from-blue-600 to-cyan-600',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    bg: 'bg-blue-500/10',
    glow: 'rgba(59, 130, 246, 0.3)',
    ring: 'ring-blue-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/fa3457810602cec18cf184dbd75abd60453a253a604ffdf7770ed4d13c980477.png',
  },
  nike: {
    name: 'Nike',
    emoji: '🏆',
    title: 'The Growth Hacker',
    gradient: 'from-yellow-400 to-orange-500',
    text: 'text-yellow-300',
    border: 'border-yellow-400/30',
    bg: 'bg-yellow-400/10',
    glow: 'rgba(250, 204, 21, 0.3)',
    ring: 'ring-yellow-400/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/b9cee3d191fe90a70097950a9bd589021b0d468cfc881960c34610f164c9f9e5.png',
  },
  metis: {
    name: 'Metis',
    emoji: '🧠',
    title: 'The Grand Strategist',
    gradient: 'from-indigo-600 to-purple-700',
    text: 'text-indigo-400',
    border: 'border-indigo-500/30',
    bg: 'bg-indigo-500/10',
    glow: 'rgba(99, 102, 241, 0.3)',
    ring: 'ring-indigo-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/be050c419cefb8db28faab388b5fe8089f6523c5b8b037dd6b03940fedc3e6d2.png',
  },
  athena: {
    name: 'Athena',
    emoji: '🦉',
    title: 'Strategic Intelligence',
    gradient: 'from-amber-500 to-orange-600',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/10',
    glow: 'rgba(245, 158, 11, 0.3)',
    ring: 'ring-amber-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/587cd4f3a933d486c920c99933004f5d3a030cb6a0e6541c5fa9ebf832661bb9.png',
  },
  hermes: {
    name: 'Hermes',
    emoji: '👟',
    title: 'The Content God',
    gradient: 'from-orange-500 to-red-600',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
    bg: 'bg-orange-500/10',
    glow: 'rgba(249, 115, 22, 0.3)',
    ring: 'ring-orange-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/daf5280c829056fa65fdd60423837c3fe88b43eec7fbd8521766bbc9e57c9e8e.png',
  },
  apollo: {
    name: 'Apollo',
    emoji: '☀️',
    title: 'God of Distribution',
    gradient: 'from-yellow-500 to-orange-500',
    text: 'text-yellow-400',
    border: 'border-yellow-500/30',
    bg: 'bg-yellow-500/10',
    glow: 'rgba(234, 179, 8, 0.3)',
    ring: 'ring-yellow-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/1221abb6c752622ec155868b92f7b758ba507b3e6791c48752ab35387fc5e71b.png',
  },
  dionysus: {
    name: 'Dionysus',
    emoji: '🍷',
    title: 'Viral Growth Master',
    gradient: 'from-purple-600 to-red-600',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    bg: 'bg-purple-500/10',
    glow: 'rgba(147, 51, 234, 0.3)',
    ring: 'ring-purple-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/02aba24b46c55d2b32790be1684424f03f43c008e4708769dbc964cfbcacfd96.png',
  },
  ares: {
    name: 'Ares',
    emoji: '🗡️',
    title: 'God of War',
    gradient: 'from-red-600 to-rose-700',
    text: 'text-red-400',
    border: 'border-red-500/30',
    bg: 'bg-red-500/10',
    glow: 'rgba(239, 68, 68, 0.3)',
    ring: 'ring-red-500/40',
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/93f5fcecbebbecd0e8145aec44f6d3b1d14838371307b563a3272c94cf716695.png',
  },
  personal_god: {
    name: 'The Thirteenth',
    emoji: '👁',
    title: 'Your Brand Consciousness',
    gradient: 'from-amber-400 via-yellow-300 to-amber-500',
    text: 'text-amber-300',
    border: 'border-amber-400/40',
    bg: 'bg-amber-500/10',
    glow: 'rgba(251, 191, 36, 0.5)',
    ring: 'ring-amber-400/50',
    portrait: null, // will use consciousness portrait
    isPersonalGod: true,
  },
};

// ═══ GOD ATMOSPHERE — unique ambient background per god while speaking ═══
const GOD_ATMOSPHERES = {
  calliope: 'radial-gradient(ellipse at 80% 20%, rgba(236,72,153,0.08) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(147,51,234,0.06) 0%, transparent 50%)',
  eris:     'radial-gradient(ellipse at 30% 70%, rgba(239,68,68,0.1) 0%, transparent 55%), radial-gradient(ellipse at 70% 30%, rgba(190,18,60,0.06) 0%, transparent 50%)',
  plutus:   'radial-gradient(ellipse at 70% 80%, rgba(34,197,94,0.08) 0%, transparent 60%), radial-gradient(ellipse at 20% 20%, rgba(16,185,129,0.05) 0%, transparent 45%)',
  hestia:   'radial-gradient(ellipse at 50% 90%, rgba(251,146,60,0.09) 0%, transparent 55%), radial-gradient(ellipse at 60% 10%, rgba(245,158,11,0.05) 0%, transparent 45%)',
  themis:   'radial-gradient(ellipse at 40% 30%, rgba(59,130,246,0.09) 0%, transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(6,182,212,0.05) 0%, transparent 50%)',
  nike:     'radial-gradient(ellipse at 60% 20%, rgba(250,204,21,0.1) 0%, transparent 50%), radial-gradient(ellipse at 30% 70%, rgba(249,115,22,0.06) 0%, transparent 50%)',
  metis:    'radial-gradient(ellipse at 50% 50%, rgba(99,102,241,0.1) 0%, transparent 60%), radial-gradient(ellipse at 10% 90%, rgba(79,70,229,0.06) 0%, transparent 45%)',
  athena:   'radial-gradient(ellipse at 20% 30%, rgba(245,158,11,0.09) 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(217,119,6,0.06) 0%, transparent 50%)',
  hermes:   'radial-gradient(ellipse at 80% 50%, rgba(249,115,22,0.09) 0%, transparent 55%), radial-gradient(ellipse at 10% 30%, rgba(239,68,68,0.05) 0%, transparent 45%)',
  apollo:   'radial-gradient(ellipse at 60% 10%, rgba(234,179,8,0.12) 0%, transparent 55%), radial-gradient(ellipse at 30% 80%, rgba(250,204,21,0.05) 0%, transparent 50%)',
  dionysus: 'radial-gradient(ellipse at 40% 60%, rgba(147,51,234,0.1) 0%, transparent 55%), radial-gradient(ellipse at 80% 20%, rgba(190,18,60,0.07) 0%, transparent 50%)',
  ares:     'radial-gradient(ellipse at 20% 80%, rgba(239,68,68,0.12) 0%, transparent 55%), radial-gradient(ellipse at 90% 30%, rgba(136,19,55,0.08) 0%, transparent 50%)',
  personal_god: 'radial-gradient(ellipse at 50% 50%, rgba(251,191,36,0.15) 0%, transparent 60%), radial-gradient(ellipse at 20% 20%, rgba(245,158,11,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(217,119,6,0.06) 0%, transparent 40%)',
};

const GodAvatar = ({ config, size = 'w-9 h-9', isSpeaking = false }) => (
  <div className={`${size} rounded-full overflow-hidden flex-shrink-0 shadow-lg relative ${isSpeaking ? 'animate-speaking-pulse' : ''}`}
       style={{ boxShadow: isSpeaking ? `0 0 20px ${config.glow}, 0 0 40px ${config.glow}` : `0 0 12px ${config.glow}` }}>
    {config.portrait ? (
      <img src={config.portrait} alt={config.name} className="w-full h-full object-cover" loading="lazy" />
    ) : (
      <div className={`w-full h-full bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
        <span className="text-sm">{config.emoji}</span>
      </div>
    )}
    <div className={`absolute inset-0 rounded-full ring-2 ${isSpeaking ? 'ring-white/60 ring-4' : config.ring} ring-opacity-50 transition-all duration-300`} />
    {isSpeaking && (
      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-green-500 border-2 border-gray-900 flex items-center justify-center">
        <Volume2 className="w-2 h-2 text-white" />
      </div>
    )}
  </div>
);

const TypingIndicator = ({ god }) => {
  const config = GOD_CONFIG[god];
  return (
    <div className="flex items-start gap-3 animate-fade-in" data-testid={`typing-${god}`}>
      <GodAvatar config={config} />
      <div className={`px-4 py-3 rounded-2xl rounded-tl-sm ${config.bg} ${config.border} border max-w-[85%]`}>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
          <span className={`text-xs ${config.text} ml-2 opacity-60`}>{config.name} is thinking...</span>
        </div>
      </div>
    </div>
  );
};

const GodMessage = ({ message, index, onPlayAudio, onListenFrom, onSideWith, playingAudioIdx, isMuted }) => {
  const isPersonalGod = message.agent === 'personal_god' || message.is_personal_god;
  const config = isPersonalGod
    ? { ...GOD_CONFIG.personal_god, name: message.personal_god_name || 'The Thirteenth' }
    : (GOD_CONFIG[message.agent] || GOD_CONFIG.calliope);
  const isUser = message.agent === 'user';
  const isAlliance = message.type === 'alliance';
  const isPlaying = playingAudioIdx === index;

  if (isUser && isAlliance) {
    const alliedGod = GOD_CONFIG[Object.keys(GOD_CONFIG).find(k => message.content.includes(GOD_CONFIG[k].name))];
    return (
      <div className="flex items-center justify-center animate-fade-in py-2" data-testid={`msg-alliance-${index}`}>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${alliedGod ? alliedGod.bg : 'bg-purple-500/10'} border ${alliedGod ? alliedGod.border : 'border-purple-500/30'}`}>
          <Swords className={`w-3.5 h-3.5 ${alliedGod ? alliedGod.text : 'text-purple-400'}`} />
          <span className={`text-xs font-medium ${alliedGod ? alliedGod.text : 'text-purple-300'}`}>{message.content}</span>
        </div>
      </div>
    );
  }

  // ── Feature #3: Dramatic Entrance notification ──
  if (message.is_entrance) {
    return (
      <div className="flex items-center gap-3 my-4 animate-god-entrance" data-testid={`entrance-${message.agent}-${index}`}>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 shadow-lg shadow-red-500/10">
          <GodAvatar config={config} size="w-6 h-6" />
          <span className={`text-xs font-bold ${config.text || 'text-white'}`}>{config.name}</span>
          <span className="text-[10px] text-gray-400">has entered the War Room</span>
          <Swords className="w-3 h-3 text-red-400" />
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex items-start gap-3 justify-end animate-slide-in-right" data-testid={`msg-user-${index}`}>
        <div className="px-4 py-3 rounded-2xl rounded-tr-sm bg-gradient-to-r from-purple-600/30 to-indigo-600/30 border border-purple-500/30 max-w-[85%]">
          <p className="text-gray-100 text-sm leading-relaxed">{message.content}</p>
        </div>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-white">You</span>
        </div>
      </div>
    );
  }

  // ── Personal God: dramatic golden entrance ──
  if (isPersonalGod) {
    return (
      <div className="animate-thirteenth-entrance" data-testid={`msg-personal-god-${index}`}>
        {/* Dramatic divider */}
        <div className="flex items-center gap-3 my-3">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-400/30">
            <Crown className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-amber-400 font-semibold uppercase tracking-widest">The Thirteenth Speaks</span>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />
        </div>
        <div className="flex items-start gap-3 animate-slide-in-left">
          {/* Golden avatar */}
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 shadow-lg relative"
               style={{ boxShadow: `0 0 25px rgba(251, 191, 36, 0.5), 0 0 50px rgba(251, 191, 36, 0.2)` }}>
            <div className="w-full h-full bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 flex items-center justify-center">
              <Eye className="w-5 h-5 text-amber-900" />
            </div>
            <div className="absolute inset-0 rounded-full ring-2 ring-amber-400/60 animate-pulse" />
          </div>
          <div className="max-w-[85%] space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-amber-300">{config.name}</span>
              <span className="text-[10px] text-amber-500/70 italic">{message.personal_god_tier || 'Awakened'}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-400 font-semibold">YOUR GOD</span>
              {message.personal_god_consciousness && (
                <span className="text-[9px] text-amber-500/50">{message.personal_god_consciousness}%</span>
              )}
            </div>
            <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-amber-500/[0.07] border border-amber-400/30 relative group"
                 style={{ boxShadow: '0 0 20px rgba(251, 191, 36, 0.1)' }}>
              <p className="text-amber-100/90 text-sm leading-relaxed">{message.content}</p>
              {!isMuted && (
                <button
                  onClick={() => onPlayAudio(message.agent, message.content, index)}
                  className={`absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-md
                    ${isPlaying
                      ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-amber-900 scale-110'
                      : 'bg-gray-800 border border-amber-500/30 text-amber-400 opacity-0 group-hover:opacity-100 hover:text-white'
                    }`}
                  title={isPlaying ? 'Stop' : `Hear ${config.name} speak`}
                  data-testid={`play-audio-personal-god-${index}`}
                >
                  {isPlaying ? <Square className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`god-entrance-wrapper ${isPlaying ? 'god-speaking' : ''}`}
         style={{ '--god-glow': config.glow, '--god-glow-strong': config.glow.replace('0.3)', '0.6)') }}
         data-testid={`msg-${message.agent}-${index}`}>
      <div className="flex items-start gap-3">
        <div className="animate-portrait-entrance">
          <GodAvatar config={config} isSpeaking={isPlaying} />
        </div>
        <div className="max-w-[85%] space-y-1 animate-message-reveal">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${config.text}`}>{config.name}</span>
            <span className="text-[10px] text-gray-600">{config.title}</span>
            {message.mood && message.mood !== 'neutral' && (
              <span className="text-[10px] text-gray-500 italic">({message.mood})</span>
            )}
            {/* Feature #15: God Confidence Score Bar */}
            {message.confidence && (
              <div className="flex items-center gap-1.5" data-testid={`confidence-${message.agent}-${index}`} title={`${message.confidence}% confident`}>
                <div className="w-12 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${
                    message.confidence >= 90 ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                    message.confidence >= 80 ? 'bg-gradient-to-r from-blue-500 to-cyan-400' :
                    'bg-gradient-to-r from-yellow-500 to-amber-400'
                  }`} style={{ width: `${message.confidence}%` }} />
                </div>
                <span className={`text-[9px] font-mono ${
                  message.confidence >= 90 ? 'text-green-400' :
                  message.confidence >= 80 ? 'text-blue-400' : 'text-yellow-400'
                }`}>{message.confidence}%</span>
              </div>
            )}
            {isPlaying && (
              <span className="text-[10px] text-green-400 flex items-center gap-1">
                <span className="inline-block w-1 h-3 bg-green-400 rounded-full animate-sound-bar-1" />
                <span className="inline-block w-1 h-3 bg-green-400 rounded-full animate-sound-bar-2" />
                <span className="inline-block w-1 h-3 bg-green-400 rounded-full animate-sound-bar-3" />
              </span>
            )}
          </div>
          <div className={`px-4 py-3 rounded-2xl rounded-tl-sm ${config.bg} ${config.border} border relative group transition-all duration-300 ${isPlaying ? 'ring-1 ring-white/10 shadow-lg' : ''}`}
               style={isPlaying ? { boxShadow: `0 0 15px ${config.glow}` } : {}}>
            <p className="text-gray-200 text-sm leading-relaxed">{message.content}</p>
            {/* TTS Play Button + Listen From Here + Side With */}
            {!isMuted && (
              <div className="absolute -bottom-2 -right-2 flex items-center gap-1">
                {/* Side with this god */}
                {!isPlaying && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onSideWith?.(message.agent, config.name); }}
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-800 border border-gray-600 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-green-400 hover:border-green-500/50 transition-all shadow-md"
                    title={`Side with ${config.name}`}
                    data-testid={`side-with-${message.agent}-${index}`}
                  >
                    <ThumbsUp className="w-3 h-3" />
                  </button>
                )}
                {/* Feature #2: Whisper to this god */}
                {!isPlaying && (
                  <button
                    onClick={(e) => { e.stopPropagation(); if (typeof window.__openWhisper === 'function') window.__openWhisper(message.agent); }}
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-800 border border-gray-600 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-purple-400 hover:border-purple-500/50 transition-all shadow-md"
                    title={`Whisper to ${config.name}`}
                    data-testid={`whisper-${message.agent}-${index}`}
                  >
                    <MessageCircle className="w-3 h-3" />
                  </button>
                )}
                {/* Listen from here — fast forward */}
                {!isPlaying && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onListenFrom?.(index); }}
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-800 border border-gray-600 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-white hover:border-gray-500 transition-all shadow-md"
                    title={`Listen from ${config.name} onward`}
                    data-testid={`listen-from-${index}`}
                  >
                    <SkipForward className="w-3 h-3" />
                  </button>
                )}
                {/* Play/stop this message */}
                <button
                  onClick={() => onPlayAudio(message.agent, message.content, index)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-md
                    ${isPlaying
                      ? `bg-gradient-to-br ${config.gradient} text-white scale-110`
                      : `bg-gray-800 border border-gray-600 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-white hover:border-gray-500`
                    }`}
                  title={isPlaying ? 'Stop' : `Hear ${config.name} speak`}
                  data-testid={`play-audio-${message.agent}-${index}`}
                >
                  {isPlaying
                    ? <Square className="w-3 h-3" />
                    : <Volume2 className="w-3 h-3" />
                  }
                </button>
              </div>
            )}
          </div>
          {/* God Reactions — ambient social dynamics */}
          {message.reactions && Object.keys(message.reactions).length > 0 && (
            <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`reactions-${message.agent}-${index}`}>
              {Object.entries(message.reactions).map(([godId, reaction]) => {
                const reactorConfig = GOD_CONFIG[godId] || { name: godId, text: 'text-gray-400', bg: 'bg-gray-700/30', border: 'border-gray-600/30' };
                return (
                  <div
                    key={godId}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${reactorConfig.bg} border ${reactorConfig.border} cursor-default transition-all duration-200 hover:scale-110 animate-reaction-pop`}
                    title={`${reactorConfig.name} reacted: ${reaction.type || 'react'}`}
                    data-testid={`reaction-${godId}-${index}`}
                  >
                    <span className="text-xs">{reaction.emoji}</span>
                    <span className={`text-[9px] font-medium ${reactorConfig.text} opacity-70`}>{reactorConfig.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LivingWarRoom = ({ API }) => {
  const { rewardEssence, pendingMilestone, dismissMilestone } = useEssenceReward();
  const { startRiver, workflow } = useWorkflow();
  const [sessionId, setSessionId] = useState(null);
  const [topic, setTopic] = useState('');
  const [messages, setMessages] = useState([]);
  const [activeGods, setActiveGods] = useState([]);
  const [visibleCount, setVisibleCount] = useState(0);
  const [typingGod, setTypingGod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [attachedUrl, setAttachedUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [responding, setResponding] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [debateEnded, setDebateEnded] = useState(false);
  const [shared, setShared] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [sharePublic, setSharePublic] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [alliedWith, setAlliedWith] = useState(null);
  const [sidingWith, setSidingWith] = useState(false);
  const [thirteenthPresence, setThirteenthPresence] = useState(null);

  // Tension Meter
  const [tensionLevel, setTensionLevel] = useState('harmonious');
  const [breakthroughMoment, setBreakthroughMoment] = useState(false);

  // Feature #2: God Whispering
  const [whisperTarget, setWhisperTarget] = useState(null);
  const [whisperMessage, setWhisperMessage] = useState('');
  const [whisperHistory, setWhisperHistory] = useState([]); // Full conversation thread
  const [whisperLoading, setWhisperLoading] = useState(false);

  // Feature #13: Industry Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);

  // Feature #19: Streak
  const [streak, setStreak] = useState(0);
  const [dailyTopic, setDailyTopic] = useState(null);

  // Feature #20: Full Pantheon
  const [godCount, setGodCount] = useState(4);

  // Feature #7: Tribunal mode
  const [tribunalMode, setTribunalMode] = useState(false);
  const [stakeholders, setStakeholders] = useState([
    { role: '', priority: '' },
    { role: '', priority: '' },
  ]);

  // Feature #6: Grudges
  const [grudges, setGrudges] = useState([]);

  // Feature #9: Split Decision
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitQuestion, setSplitQuestion] = useState('');
  const [splitResult, setSplitResult] = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);

  // Feature #16: Footnotes
  const [footnotes, setFootnotes] = useState(null);
  const [footnotesLoading, setFootnotesLoading] = useState(false);

  // Feature #17: Debate Pace
  const [debatePace, setDebatePace] = useState(null);

  // Feature #8: Instant Replay
  const [replayNarration, setReplayNarration] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayAudio, setReplayAudio] = useState(null); // Audio segments
  const [replayPlaying, setReplayPlaying] = useState(null); // Currently playing audio index

  // Feature #18: The Oracle
  const [oraclePrediction, setOraclePrediction] = useState(null);
  const [oracleLoading, setOracleLoading] = useState(false);
  const [oracleProphecy, setOracleProphecy] = useState(null); // Pre-debate prophecy
  const [oracleProphecyLoading, setOracleProphecyLoading] = useState(false);

  // Feature #9: Split Decision Continue
  const [splitContinueLoading, setSplitContinueLoading] = useState(false);

  // Feature #14: Streaming Debate
  const [streamingMode, setStreamingMode] = useState(false);
  const [streamingMessages, setStreamingMessages] = useState([]);
  const [streamingGod, setStreamingGod] = useState(null);
  const [streamingText, setStreamingText] = useState('');

  // Feature #11: Joint War Room
  const [jointMode, setJointMode] = useState(false);
  const [jointWs, setJointWs] = useState(null);
  const [jointParticipants, setJointParticipants] = useState(0);
  const [jointInviteLink, setJointInviteLink] = useState(null);

  // Feature #12: The Challenge
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeFeed, setChallengeFeed] = useState([]);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [challengeResult, setChallengeResult] = useState(null);

  // Feature #10: Leaderboard
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);


  // Video Export
  const [exportingVideo, setExportingVideo] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // TTS Audio
  const [playingAudioIdx, setPlayingAudioIdx] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [autoNarrate, setAutoNarrate] = useState(false);
  const [isNarrating, setIsNarrating] = useState(false);
  const narrationQueueRef = useRef([]);
  const autoNarratePendingRef = useRef(null);
  const cinematicRef = useRef(false); // tracks if cinematic reveal is in progress

  // Topic selection
  const [showTopicSelection, setShowTopicSelection] = useState(true);
  const [customTopic, setCustomTopic] = useState('');

  // Pre-fill from River workflow
  useEffect(() => {
    if (workflow.active && workflow.targetTool === 'alliance' && workflow.topic) {
      setCustomTopic(workflow.topic);
    }
  }, [workflow]);

  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const animationRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  // TTS Audio playback — returns a promise that resolves when audio finishes
  const handlePlayAudio = useCallback(async (godId, text, msgIdx) => {
    if (isMuted) return;

    // If same message is playing, stop it
    if (playingAudioIdx === msgIdx) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioIdx(null);
      setIsNarrating(false);
      narrationQueueRef.current = [];
      stopGodTheme();
      return;
    }

    // Stop any current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setPlayingAudioIdx(msgIdx);
    setLoadingAudio(true);

    try {
      const res = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, god_id: godId }),
      });

      if (!res.ok) throw new Error('TTS failed');

      const data = await res.json();
      const audio = new Audio(data.audio);
      audioRef.current = audio;

      // Play god's theme music softly under voice
      if (!isMuted) playGodTheme(godId, 0.45);

      return new Promise((resolve) => {
        audio.onended = () => {
          setPlayingAudioIdx(null);
          audioRef.current = null;
          stopGodTheme();
          // If narrating via Listen button (non-cinematic), play next in queue
          if (narrationQueueRef.current.length > 0) {
            const next = narrationQueueRef.current.shift();
            setTimeout(() => handlePlayAudio(next.godId, next.text, next.idx), 400);
          } else {
            setIsNarrating(false);
          }
          resolve();
        };
        audio.onerror = () => {
          setPlayingAudioIdx(null);
          audioRef.current = null;
          stopGodTheme();
          setIsNarrating(false);
          narrationQueueRef.current = [];
          resolve(); // resolve so cinematic can continue
        };
        audio.play().catch(() => {
          setPlayingAudioIdx(null);
          audioRef.current = null;
          stopGodTheme();
          resolve();
        });
      });
    } catch (err) {
      console.error('[TTS] Error:', err);
      setPlayingAudioIdx(null);
      setIsNarrating(false);
      narrationQueueRef.current = [];
    } finally {
      setLoadingAudio(false);
    }
  }, [playingAudioIdx, isMuted]);

  // Play topic announcement via TTS (uses "metis" — deep, contemplative narrator)
  const playTopicTTS = useCallback(async (topicText) => {
    if (isMuted) return;
    try {
      const res = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: `The council deliberates: ${topicText}`, god_id: 'metis' }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const audio = new Audio(data.audio);
      audioRef.current = audio;
      if (!isMuted) playGodTheme('metis', 0.35);
      return new Promise((resolve) => {
        audio.onended = () => { audioRef.current = null; stopGodTheme(); resolve(); };
        audio.onerror = () => { audioRef.current = null; stopGodTheme(); resolve(); };
        audio.play().catch(() => { audioRef.current = null; resolve(); });
      });
    } catch { /* topic TTS failed, continue anyway */ }
  }, [isMuted]);

  // Listen to Full Debate — topic first, then sequential playback with theme music
  const handleListenToDebate = useCallback(async () => {
    if (isNarrating) {
      // Stop narration
      cinematicRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      stopGodTheme();
      setPlayingAudioIdx(null);
      setIsNarrating(false);
      narrationQueueRef.current = [];
      return;
    }

    const godMessages = messages
      .map((msg, i) => ({ ...msg, idx: i }))
      .filter(msg => msg.agent !== 'user' && msg.type !== 'alliance');

    if (godMessages.length === 0) return;

    setIsNarrating(true);

    // Step 1: Read topic aloud first
    if (topic && !isMuted) {
      await playTopicTTS(topic);
      await new Promise(r => setTimeout(r, 500));
    }

    // Step 2: Queue remaining messages, play first immediately
    narrationQueueRef.current = godMessages.slice(1).map(msg => ({
      godId: msg.agent,
      text: msg.content,
      idx: msg.idx,
    }));

    const first = godMessages[0];
    handlePlayAudio(first.agent, first.content, first.idx);
  }, [messages, topic, isNarrating, handlePlayAudio, playTopicTTS, isMuted]);

  // Listen from a specific message — click-to-fast-forward
  const handleListenFrom = useCallback((startIdx) => {
    if (isMuted) return;

    // Stop any current playback
    cinematicRef.current = false;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    stopGodTheme();

    const godMessages = messages
      .map((msg, i) => ({ ...msg, idx: i }))
      .filter(msg => msg.agent !== 'user' && msg.type !== 'alliance' && msg.idx >= startIdx);

    if (godMessages.length === 0) return;

    setIsNarrating(true);

    // Queue all messages after the first, play first immediately
    narrationQueueRef.current = godMessages.slice(1).map(msg => ({
      godId: msg.agent,
      text: msg.content,
      idx: msg.idx,
    }));

    const first = godMessages[0];
    handlePlayAudio(first.agent, first.content, first.idx);
  }, [messages, isMuted, handlePlayAudio]);

  // Auto-narrate: when a new message appears and autoNarrate is on, play it
  useEffect(() => {
    if (!autoNarrate || isMuted || visibleCount === 0) return;
    // Don't interfere with cinematic mode
    if (cinematicRef.current) return;
    const lastVisible = messages[visibleCount - 1];
    if (!lastVisible || lastVisible.agent === 'user' || lastVisible.type === 'alliance') return;
    // Don't auto-play if already playing
    if (playingAudioIdx !== null) return;

    if (autoNarratePendingRef.current) clearTimeout(autoNarratePendingRef.current);
    autoNarratePendingRef.current = setTimeout(() => {
      handlePlayAudio(lastVisible.agent, lastVisible.content, visibleCount - 1);
    }, 800);

    return () => {
      if (autoNarratePendingRef.current) clearTimeout(autoNarratePendingRef.current);
    };
  // eslint-disable-next-line
  }, [visibleCount, autoNarrate, isMuted]);

  // ═══ VOICE INPUT — Browser Native STT for topic, Whisper for chat ═══
  const [isRecordingTopic, setIsRecordingTopic] = useState(false);
  const [isRecordingChat, setIsRecordingChat] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const topicRecognitionRef = useRef(null);
  const chatMediaRecorderRef = useRef(null);
  const chatAudioChunksRef = useRef([]);

  // Browser native speech recognition for topic input
  const toggleTopicVoice = useCallback(() => {
    if (isRecordingTopic) {
      topicRecognitionRef.current?.stop();
      setIsRecordingTopic(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      const { toast } = require('sonner');
      toast.error('Speech recognition not supported in this browser. Try Chrome or Edge.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    let finalTranscript = customTopic;
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? ' ' : '') + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setCustomTopic(finalTranscript + (interim ? ' ' + interim : ''));
    };
    recognition.onerror = () => setIsRecordingTopic(false);
    recognition.onend = () => setIsRecordingTopic(false);
    topicRecognitionRef.current = recognition;
    recognition.start();
    setIsRecordingTopic(true);
  }, [isRecordingTopic, customTopic]);

  // Whisper-powered voice recording for chat input
  const toggleChatVoice = useCallback(async () => {
    if (isRecordingChat) {
      chatMediaRecorderRef.current?.stop();
      setIsRecordingChat(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chatAudioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chatAudioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chatAudioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) return;
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'voice.webm');
          const res = await fetch(`${API_URL}/api/stt/transcribe`, { method: 'POST', body: formData });
          if (res.ok) {
            const data = await res.json();
            if (data.text?.trim()) setUserInput(prev => (prev ? prev + ' ' : '') + data.text.trim());
          } else {
            const { toast } = await import('sonner');
            toast.error('Voice transcription failed. Try again.');
          }
        } catch {
          const { toast } = await import('sonner');
          toast.error('Voice transcription error.');
        } finally {
          setIsTranscribing(false);
        }
      };

      chatMediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecordingChat(true);
    } catch {
      const { toast } = await import('sonner');
      toast.error('Microphone access denied. Please allow mic permissions.');
    }
  }, [isRecordingChat]);

  // Cleanup voice on unmount
  useEffect(() => {
    return () => {
      topicRecognitionRef.current?.stop();
      chatMediaRecorderRef.current?.stop();
    };
  }, []);

  // Topic selection handlers
  const handleSeekCounsel = () => {
    topicRecognitionRef.current?.stop();
    setIsRecordingTopic(false);
    setShowTopicSelection(false);
    setLoading(true);
    launchDebate(null);
  };

  const handleIssueChallenge = () => {
    if (!customTopic.trim()) return;
    topicRecognitionRef.current?.stop();
    setIsRecordingTopic(false);
    setShowTopicSelection(false);
    setLoading(true);
    
    // Feature #7: If Tribunal mode, use the tribunal endpoint
    if (tribunalMode) {
      const validStakeholders = stakeholders.filter(s => s.role.trim());
      if (validStakeholders.length > 0) {
        handleTribunalLaunch(customTopic.trim(), validStakeholders);
        return;
      }
    }
    
    launchDebate(customTopic.trim());
  };

  // Feature #7: Tribunal launch
  const handleTribunalLaunch = async (topic, validStakeholders) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/tribunal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ custom_topic: topic, stakeholders: validStakeholders, god_count: godCount })
      });
      if (!res.ok) throw new Error('Tribunal failed');
      const data = await res.json();
      setSessionId(data.session_id);
      setTopic(data.topic);
      setMessages(data.messages || []);
      setVerdict(null);
      setTensionLevel(data.tension_level || 'harmonious');
      setLoading(false);
    } catch (err) {
      console.error('[Tribunal] Error:', err);
      setLoading(false);
    }
  };

  // Feature #2: Whisper to a god privately
  const handleWhisper = async (godId) => {
    if (!whisperMessage.trim() || !sessionId) return;
    const userMsg = whisperMessage.trim();
    setWhisperHistory(prev => [...prev, { role: 'user', content: userMsg }]);
    setWhisperMessage('');
    setWhisperLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/whisper`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: sessionId, god_id: godId, message: userMsg })
      });
      if (!res.ok) throw new Error('Whisper failed');
      const data = await res.json();
      setWhisperHistory(prev => [...prev, { role: 'god', content: data.response, god_name: data.god_name }]);
    } catch (err) {
      console.error('[Whisper] Error:', err);
      setWhisperHistory(prev => [...prev, { role: 'god', content: 'The god is momentarily distracted by the debate...', god_name: '' }]);
    } finally {
      setWhisperLoading(false);
    }
  };

  // Feature #5: Download The Scroll (PDF)
  const handleDownloadScroll = async () => {
    if (!sessionId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/${sessionId}/scroll`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Scroll failed');
      
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/pdf')) {
        // PDF response — download directly
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `war-room-scroll-${sessionId.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // Fallback markdown
        const data = await res.json();
        const blob = new Blob([data.scroll_content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `war-room-scroll-${sessionId.slice(0, 8)}.md`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('[Scroll] Error:', err);
    }
  };

  // Feature #9: Split Decision
  const handleSplitDecision = async () => {
    if (!splitQuestion.trim() || !sessionId) return;
    setSplitLoading(true);
    setSplitResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: sessionId, fork_question: splitQuestion })
      });
      if (!res.ok) throw new Error('Split failed');

      // Consume SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamResult = { fork_question: splitQuestion, path_a: { label: 'Path A: Yes', messages: [] }, path_b: { label: 'Path B: No', messages: [] } };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'message_complete') {
              const pathKey = event.path === 'A' ? 'path_a' : 'path_b';
              streamResult = {
                ...streamResult,
                [pathKey]: {
                  ...streamResult[pathKey],
                  messages: [...streamResult[pathKey].messages, event.message]
                }
              };
              setSplitResult({ ...streamResult });
            } else if (event.type === 'split_complete') {
              // Final complete event — use as definitive result
              setSplitResult({
                fork_question: event.fork_question,
                path_a: event.path_a,
                path_b: event.path_b
              });
            }
          } catch {}
        }
      }
      setSplitQuestion('');
    } catch (err) {
      console.error('[Split] Error:', err);
    } finally {
      setSplitLoading(false);
    }
  };

  // Feature #16: Footnotes
  const handleFootnotes = async () => {
    if (!sessionId) return;
    setFootnotesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/${sessionId}/footnotes`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Footnotes failed');
      const data = await res.json();
      setFootnotes(data.footnotes);
    } catch (err) {
      console.error('[Footnotes] Error:', err);
    } finally {
      setFootnotesLoading(false);
    }
  };

  // Feature #17: Fetch debate pace
  const fetchDebatePace = async () => {
    if (!sessionId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/${sessionId}/pace`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setDebatePace(data);
      }
    } catch (err) { /* silent */ }
  };

  // Feature #8: Instant Replay with Audio
  const handleInstantReplay = async () => {
    if (!sessionId) return;
    setReplayLoading(true);
    setReplayAudio(null);
    try {
      const token = localStorage.getItem('token');
      // Try audio replay first
      const audioRes = await fetch(`${API_URL}/api/war-council/living-room/${sessionId}/replay-audio`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (audioRes.ok) {
        const data = await audioRes.json();
        setReplayNarration(data.narration_script);
        setReplayAudio({
          narration: data.narration_audio_base64,
          segments: data.god_audio_segments || [],
          format: data.format || 'mp3',
        });
      } else {
        // Fallback to text-only replay
        const res = await fetch(`${API_URL}/api/war-council/living-room/${sessionId}/replay`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error('Replay failed');
        const data = await res.json();
        setReplayNarration(data.replay_narration);
      }
    } catch (err) {
      console.error('[Replay] Error:', err);
    } finally {
      setReplayLoading(false);
    }
  };

  // Play a specific audio segment from replay
  const playReplayAudio = (audioBase64, index) => {
    try {
      if (replayPlaying !== null) {
        // Stop current
        setReplayPlaying(null);
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      }
      const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
      audioRef.current = audio;
      setReplayPlaying(index);
      audio.onended = () => { setReplayPlaying(null); audioRef.current = null; };
      audio.onerror = () => { setReplayPlaying(null); audioRef.current = null; };
      audio.play().catch(() => setReplayPlaying(null));
    } catch (err) {
      console.error('[Replay Audio] Play error:', err);
    }
  };

  // Feature #18: The Oracle — Post-debate analysis
  const handleOracle = async () => {
    if (!sessionId) return;
    setOracleLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/${sessionId}/oracle`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Oracle failed');
      const data = await res.json();
      setOraclePrediction(data.prediction);
    } catch (err) {
      console.error('[Oracle] Error:', err);
    } finally {
      setOracleLoading(false);
    }
  };

  // Feature #18: Oracle PROPHECY — Pre-debate prediction
  const handleOracleProphecy = async (topicText, godIds) => {
    if (!topicText) return;
    setOracleProphecyLoading(true);
    setOracleProphecy(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/oracle/prophecy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ topic: topicText, god_ids: godIds, god_count: godCount })
      });
      if (!res.ok) throw new Error('Prophecy failed');
      const data = await res.json();
      setOracleProphecy(data.prophecy);
    } catch (err) {
      console.error('[Oracle Prophecy] Error:', err);
    } finally {
      setOracleProphecyLoading(false);
    }
  };

  // Feature #9: Split Decision — Continue from fork
  const handleContinueFromFork = async (chosenPath, forkQuestion, pathMessages) => {
    if (!sessionId) return;
    setSplitContinueLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/split/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          parent_session_id: sessionId,
          chosen_path: chosenPath,
          fork_question: forkQuestion,
          path_messages: pathMessages,
        })
      });
      if (!res.ok) throw new Error('Fork continue failed');
      const data = await res.json();
      // Switch to the forked session
      setSessionId(data.session_id);
      setMessages(data.messages || []);
      setVisibleCount(data.messages?.length || 0);
      setTension(data.tension_level || 'medium');
      setSplitResult(null);
      setSplitQuestion('');
    } catch (err) {
      console.error('[Split Continue] Error:', err);
    } finally {
      setSplitContinueLoading(false);
    }
  };

  // Feature #14: Start Streaming Debate via SSE
  const startStreamingDebate = async (topicText) => {
    if (!topicText) return;
    setStreamingMode(true);
    setStreamingMessages([]);
    setStreamingGod(null);
    setStreamingText('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/war-council/living-room/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_topic: topicText, god_count: godCount }),
      });

      if (!res.ok) throw new Error('Streaming failed');
      
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            
            switch (event.type) {
              case 'session_start':
                setSessionId(event.session_id);
                setTopic(event.topic);
                setIsLoading(false);
                break;
              case 'typing_start':
                setStreamingGod(event.god_id);
                setStreamingText('');
                setTypingGod(event.god_id);
                break;
              case 'token':
                setStreamingText(event.full_text);
                setStreamingGod(event.god_id);
                scrollToBottom();
                break;
              case 'message_complete':
                setStreamingGod(null);
                setStreamingText('');
                setTypingGod(null);
                setMessages(prev => [...prev, event.message]);
                setVisibleCount(prev => prev + 1);
                scrollToBottom();
                break;
              case 'god_entrance':
                setMessages(prev => [...prev, {
                  agent: event.god_id,
                  content: '',
                  is_entrance: true,
                  entrance_name: event.god_name,
                }]);
                setVisibleCount(prev => prev + 1);
                break;
              case 'session_complete':
                setStreamingMode(false);
                break;
              default:
                break;
            }
          } catch { /* skip malformed events */ }
        }
      }
    } catch (err) {
      console.error('[Streaming] Error:', err);
      setStreamingMode(false);
      setIsLoading(false);
    }
  };

  // Feature #11: Joint War Room — WebSocket connection
  const startJointSession = (topicText) => {
    const newSessionId = 'joint_' + Math.random().toString(36).substr(2, 9);
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const userName = localStorage.getItem('userName') || 'Strategist';
    const ws = new WebSocket(`${wsUrl}/api/war-council/living-room/ws/${newSessionId}?user_name=${encodeURIComponent(userName)}`);

    ws.onopen = () => {
      setJointMode(true);
      setJointWs(ws);
      setSessionId(newSessionId);
      setJointInviteLink(`${window.location.origin}${window.location.pathname}?joint=${newSessionId}`);
      ws.send(JSON.stringify({ type: 'start_debate', topic: topicText, god_count: godCount }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'user_joined':
        case 'user_left':
          setJointParticipants(data.participant_count);
          break;
        case 'debate_started':
          setTopic(data.topic);
          setIsLoading(false);
          break;
        case 'god_typing':
          setTypingGod(data.god_id);
          break;
        case 'god_response':
          setTypingGod(null);
          setMessages(prev => [...prev, data.message]);
          setVisibleCount(prev => prev + 1);
          scrollToBottom();
          break;
        case 'user_message':
          if (data.user_name !== userName) {
            setMessages(prev => [...prev, { agent: 'user', content: data.message, user_name: data.user_name }]);
            setVisibleCount(prev => prev + 1);
          }
          break;
        case 'session_state':
          setTopic(data.topic);
          setMessages(data.messages || []);
          setVisibleCount(data.messages?.length || 0);
          setJointParticipants(data.participant_count);
          break;
        default:
          break;
      }
    };

    ws.onerror = (err) => {
      console.error('[Joint WR] WebSocket error:', err);
      setJointMode(false);
    };

    ws.onclose = () => {
      setJointMode(false);
      setJointWs(null);
    };
  };

  // Send message in joint session
  const sendJointMessage = (msg) => {
    if (jointWs && jointWs.readyState === WebSocket.OPEN) {
      jointWs.send(JSON.stringify({ type: 'user_message', message: msg }));
    }
  };

  // Feature #12: Publish verdict for public challenge
  const handlePublishVerdict = async () => {
    if (!sessionId) return;
    setPublishLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userName = localStorage.getItem('userName') || 'Strategist';
      const res = await fetch(`${API_URL}/api/war-council/challenges/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ session_id: sessionId, user_name: userName })
      });
      if (!res.ok) throw new Error('Publish failed');
      const data = await res.json();
      const { toast } = await import('sonner');
      toast.success(`Verdict published! Challenge ID: ${data.challenge_id}`);
    } catch (err) {
      console.error('[Challenge] Publish error:', err);
    } finally {
      setPublishLoading(false);
    }
  };

  // Feature #12: Load challenge feed
  const loadChallengeFeed = async () => {
    try {
      const res = await fetch(`${API_URL}/api/war-council/challenges/feed`);
      if (res.ok) {
        const data = await res.json();
        setChallengeFeed(data.challenges || []);
      }
    } catch {}
  };

  // Feature #10: Load leaderboard data
  const loadLeaderboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/leaderboard`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setLeaderboardData(data.leaderboard || []);
      }
    } catch {}
  };

  // Feature #12: Accept a challenge
  const handleAcceptChallenge = async (challengeId) => {
    setChallengeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userName = localStorage.getItem('userName') || 'Rival Strategist';
      const res = await fetch(`${API_URL}/api/war-council/challenges/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ challenge_id: challengeId, challenger_name: userName, god_count: godCount })
      });
      if (!res.ok) throw new Error('Challenge failed');
      const data = await res.json();
      setChallengeResult(data);
      // Optionally navigate to the new session
      setSessionId(data.session_id);
      setTopic(data.topic);
      setMessages(data.counter_arguments || []);
      setVisibleCount(data.counter_arguments?.length || 0);
      setActiveGods(data.challenger_gods || []);
      setShowChallengeModal(false);
      setShowTopicSelection(false);
      setLoading(false);
    } catch (err) {
      console.error('[Challenge] Accept error:', err);
    } finally {
      setChallengeLoading(false);
    }
  };

  // Core debate launcher (called after topic selection)
  const launchDebate = async (customTopicOverride) => {
    setError(null);
    setSaved(false);
    setShared(null);
    setOracleProphecy(null);
    
    const topicText = customTopicOverride || '';
    
    // Feature #18: Fire Oracle Prophecy BEFORE debate starts (non-blocking)
    if (topicText) {
      handleOracleProphecy(topicText);
    }

    try {
      const token = localStorage.getItem('token');
      const body = customTopicOverride ? { custom_topic: customTopicOverride, god_count: godCount } : { god_count: godCount };
      const res = await fetch(`${API_URL}/api/war-council/living-room`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Failed to start living room');
      const data = await res.json();
      setSessionId(data.session_id);
      setTopic(data.topic);
      setMessages(data.messages || []);
      setActiveGods(data.gods || []);
      setVerdict(data.verdict || null);
      setPendingActions(data.pending_actions || []);
      setThirteenthPresence(data.thirteenth_presence || null);
      setTensionLevel(data.tension_level || 'harmonious');
      setBreakthroughMoment(data.breakthrough_moment || false);
      setLoading(false);
      if (!isMuted) playGavel(0.4);
      animateMessages(data.messages || [], data.topic);
      // Reward essence for starting a War Council debate
      rewardEssence('war_council_message');
    } catch (err) {
      console.error('[LivingWarRoom] Error:', err);
      setError('The gods are resting. Try refreshing.');
      setLoading(false);
    }
  };

  // No auto-start — debate launches after topic selection
  useEffect(() => {
    // Fetch templates and streak on mount
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    fetch(`${API_URL}/api/war-council/living-room/templates`, { headers })
      .then(r => r.json()).then(d => setTemplates(d.templates || [])).catch(() => {});
    
    fetch(`${API_URL}/api/war-council/living-room/streak`, { headers })
      .then(r => r.json()).then(d => {
        setStreak(d.streak || 0);
        setDailyTopic(d.daily_topic || null);
      }).catch(() => {});
    
    return () => {
      if (animationRef.current) clearTimeout(animationRef.current);
      cinematicRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      stopGodTheme();
    };
  // eslint-disable-next-line
  }, []);

  // Feature #2: Register whisper handler for god message buttons
  useEffect(() => {
    window.__openWhisper = (godId) => {
      setWhisperTarget(godId);
      setWhisperHistory([]); // Fresh thread per god
      setWhisperMessage('');
    };
    return () => { delete window.__openWhisper; };
  }, []);

  // Feature #17: Auto-fetch debate pace when messages change
  useEffect(() => {
    if (sessionId && messages.length > 0) {
      fetchDebatePace();
    }
  // eslint-disable-next-line
  }, [sessionId, messages.length]);

  // Play verdict sound when verdict panel appears
  const verdictPlayedRef = useRef(false);
  useEffect(() => {
    if (verdict && verdict.verdict && visibleCount >= messages.length && !typingGod && !verdictPlayedRef.current) {
      verdictPlayedRef.current = true;
      if (!isMuted) {
        playGavel(0.3);
        setTimeout(() => playVerdict(0.3), 300);
      }
    }
    if (!verdict) verdictPlayedRef.current = false;
  }, [verdict, visibleCount, messages.length, typingGod, isMuted]);

  // ═══ CINEMATIC DEBATE REVEAL ═══
  // When audio is ON: topic TTS → each god speaks one at a time with theme music
  // When muted: fast text reveal (original behavior)
  const animateMessages = useCallback((msgs, topicText, startOffset = 0) => {
    if (!msgs.length) return;

    // Feature #1: Tension-based animation speed — gods speak faster at high tension
    const tensionSpeed = tensionLevel === 'volcanic' ? 0.4 : tensionLevel === 'heated' ? 0.6 : tensionLevel === 'intense' ? 0.8 : 1.0;
    const baseDelay = Math.round(1500 * tensionSpeed);
    const baseGap = Math.round(800 * tensionSpeed);

    if (isMuted) {
      // Fast reveal — no audio
      let step = 0;
      const showNext = () => {
        if (step < msgs.length) {
          setTypingGod(msgs[step]?.agent || 'calliope');
          animationRef.current = setTimeout(() => {
            setTypingGod(null);
            setVisibleCount(prev => prev + 1);
            step++;
            setTimeout(scrollToBottom, 50);
            if (step < msgs.length) {
              animationRef.current = setTimeout(showNext, baseGap);
            }
          }, baseDelay);
        }
      };
      animationRef.current = setTimeout(showNext, 600);
      return;
    }

    // ─── CINEMATIC MODE ─── 
    cinematicRef.current = true;
    setIsNarrating(true);

    const runCinematic = async () => {
      // Step 1: Brief pause, then announce topic via TTS
      await new Promise(r => setTimeout(r, 800));

      if (!cinematicRef.current) return;

      // Read topic aloud (only for the initial round, not follow-ups)
      if (topicText && startOffset === 0) {
        try {
          const topicRes = await fetch(`${API_URL}/api/tts/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: `The council deliberates: ${topicText}`, god_id: 'metis' }),
          });
          if (topicRes.ok && cinematicRef.current) {
            const topicData = await topicRes.json();
            const topicAudio = new Audio(topicData.audio);
            audioRef.current = topicAudio;
            playGodTheme('metis', 0.1);
            await new Promise((resolve) => {
              topicAudio.onended = () => { audioRef.current = null; stopGodTheme(); resolve(); };
              topicAudio.onerror = () => { audioRef.current = null; stopGodTheme(); resolve(); };
              topicAudio.play().catch(() => { audioRef.current = null; resolve(); });
            });
          }
        } catch { /* topic TTS failed, continue */ }
        await new Promise(r => setTimeout(r, 500));
      }

      // Step 2: Reveal each message one-at-a-time with TTS + theme
      for (let i = 0; i < msgs.length; i++) {
        if (!cinematicRef.current) break;

        const msg = msgs[i];
        const godId = msg.agent || 'calliope';
        const isGodMessage = msg.agent !== 'user' && msg.type !== 'alliance';
        // Absolute index in the full messages array
        const absIdx = startOffset + i;

        // Show typing indicator
        setTypingGod(godId);
        scrollToBottom();

        if (isGodMessage) {
          // Fetch TTS while typing indicator is showing
          let audioEl = null;
          try {
            const ttsRes = await fetch(`${API_URL}/api/tts/generate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: msg.content, god_id: godId }),
            });
            if (ttsRes.ok) {
              const ttsData = await ttsRes.json();
              audioEl = new Audio(ttsData.audio);
            }
          } catch { /* TTS failed for this message */ }

          if (!cinematicRef.current) break;

          // Reveal the message text
          setTypingGod(null);
          setVisibleCount(prev => prev + 1);
          setPlayingAudioIdx(absIdx);
          setTimeout(scrollToBottom, 50);

          // Play TTS + theme music
          if (audioEl && cinematicRef.current) {
            audioRef.current = audioEl;
            playGodTheme(godId, 0.15);
            await new Promise((resolve) => {
              audioEl.onended = () => {
                setPlayingAudioIdx(null);
                audioRef.current = null;
                stopGodTheme();
                resolve();
              };
              audioEl.onerror = () => {
                setPlayingAudioIdx(null);
                audioRef.current = null;
                stopGodTheme();
                resolve();
              };
              audioEl.play().catch(() => {
                setPlayingAudioIdx(null);
                audioRef.current = null;
                stopGodTheme();
                resolve();
              });
            });
          } else {
            // No audio — just pause to let the message be read
            await new Promise(r => setTimeout(r, 2500));
          }

          // Brief pause between gods
          if (i < msgs.length - 1 && cinematicRef.current) {
            await new Promise(r => setTimeout(r, 600));
          }
        } else {
          // Non-god message (user/alliance) — just reveal quickly
          await new Promise(r => setTimeout(r, 800));
          setTypingGod(null);
          setVisibleCount(prev => prev + 1);
          setTimeout(scrollToBottom, 50);
        }
      }

      cinematicRef.current = false;
      setIsNarrating(false);
      setPlayingAudioIdx(null);
    };

    runCinematic();
  }, [isMuted, scrollToBottom]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim() || !sessionId || responding) return;
    
    // Cancel current cinematic playback before user interjects
    cinematicRef.current = false;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    stopGodTheme();
    setIsNarrating(false);
    setPlayingAudioIdx(null);
    narrationQueueRef.current = [];
    
    const msg = userInput.trim();
    const url = attachedUrl.trim();
    setUserInput('');
    setAttachedUrl('');
    setShowUrlInput(false);
    setResponding(true);
    
    // Add user message immediately (with URL indicator if present)
    const displayContent = url ? `${msg}\n\n📎 Attached: ${url}` : msg;
    const userMsg = { agent: 'user', content: displayContent };
    setMessages(prev => [...prev, userMsg]);
    setVisibleCount(prev => prev + 1);
    
    setTimeout(scrollToBottom, 50);
    
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({ user_message: msg });
      if (url) queryParams.append('context_url', url);
      const res = await fetch(`${API_URL}/api/war-council/living-room/${sessionId}/respond?${queryParams}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!res.ok) throw new Error('Response failed');
      
      const data = await res.json();
      const newMsgs = data.messages || [];
      
      // Update tension meter
      if (data.tension_level) setTensionLevel(data.tension_level);
      if (data.breakthrough_moment !== undefined) setBreakthroughMoment(data.breakthrough_moment);
      
      // Check if debate was ended by user farewell
      if (data.debate_ended) {
        setMessages(prev => [...prev, ...newMsgs]);
        setVisibleCount(prev => prev + newMsgs.length);
        // Play farewell messages cinematically but mark debate as ended
        const currentTotal = messages.length + 1;
        await animateMessages(newMsgs, null, currentTotal);
        setDebateEnded(true);
        return;
      }
      
      // Add new messages and animate them with cinematic TTS
      // startOffset = current total messages (user msg + previous msgs)
      const currentTotal = messages.length + 1; // +1 for the user message just added
      setMessages(prev => [...prev, ...newMsgs]);
      animateMessages(newMsgs, null, currentTotal);
      // Reward essence for participating in the debate
      rewardEssence('war_council_message');
      
    } catch (err) {
      console.error('[LivingWarRoom] Response error:', err);
    } finally {
      setResponding(false);
    }
  };

  const handleRefresh = () => {
    cinematicRef.current = false;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    stopGodTheme();
    narrationQueueRef.current = [];
    verdictPlayedRef.current = false;
    setMessages([]);
    setVisibleCount(0);
    setTypingGod(null);
    setTopic('');
    setSessionId(null);
    setActiveGods([]);
    setLoading(false);
    setError(null);
    setSaved(false);
    setShared(null);
    setVerdict(null);
    setPendingActions([]);
    setAlliedWith(null);
    setDebateEnded(false);
    setTensionLevel('harmonious');
    setBreakthroughMoment(false);
    setIsRecordingTopic(false);
    setIsRecordingChat(false);
    setIsTranscribing(false);
    topicRecognitionRef.current?.stop();
    chatMediaRecorderRef.current?.stop();
    setShowTopicSelection(true);
    setCustomTopic('');
    setPlayingAudioIdx(null);
    setIsNarrating(false);
    setThirteenthPresence(null);
    setSharePublic(false);
  };

  const handleSave = async () => {
    if (!sessionId || saved) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ session_id: sessionId, share_public: sharePublic })
      });
      if (res.ok) {
        setSaved(true);
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(`Save failed: ${errData.detail || 'Could not save debate. Please try again.'}`);
      }
    } catch (err) {
      console.error('[LivingWarRoom] Save error:', err);
      alert('Failed to save debate. Please try again.');
    }
  };

  const handleShare = async () => {
    if (!sessionId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ session_id: sessionId })
      });
      if (res.ok) {
        const data = await res.json();
        setShared(data.share_id);
        const url = `${window.location.origin}/living-room/${data.share_id}`;
        // Try clipboard first, fall back to prompt
        try {
          await navigator.clipboard.writeText(url);
          setCopyFeedback(true);
          setTimeout(() => setCopyFeedback(false), 3000);
        } catch {
          // Clipboard failed (mobile/http) — show prompt with the URL
          window.prompt('Share link (copy it below):', url);
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(`Share failed: ${errData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[LivingWarRoom] Share error:', err);
      alert('Failed to create share link. Please try again.');
    }
  };

  const handleSideWith = async (godId) => {
    if (!sessionId || sidingWith || alliedWith) return;
    setSidingWith(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/side-with`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ session_id: sessionId, god_id: godId })
      });
      if (res.ok) {
        const data = await res.json();
        setAlliedWith(godId);
        if (!isMuted) playAlliance(godId, 0.2);
        // Add alliance marker immediately
        const allianceMsg = { agent: 'user', content: `Sided with ${GOD_CONFIG[godId]?.name || godId}`, type: 'alliance' };
        const reactions = data.reactions || [];
        const currentTotal = messages.length;
        setMessages(prev => [...prev, allianceMsg, ...reactions]);
        // Show alliance marker immediately
        setVisibleCount(prev => prev + 1);
        setTimeout(scrollToBottom, 100);
        // Animate reaction messages with cinematic TTS (one at a time)
        if (reactions.length > 0) {
          animateMessages(reactions, null, currentTotal + 1);
        }
        // Reward extra essence for committing to an alliance
        rewardEssence('war_council_message', 2.0);
      }
    } catch (err) {
      console.error('[LivingWarRoom] Side-with error:', err);
    } finally {
      setSidingWith(false);
    }
  };

  // ── Per-message "Sided With" affinity tracking ──
  const handleAffinitySideWith = useCallback(async (godId, godName) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const uid = payload.sub || payload.user_id;
      await fetch(`${API_URL}/api/avatar/sided-with`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_id: uid, god_name: godName || godId, debate_id: sessionId })
      });
      // Visual feedback via toast
      const { toast } = await import('sonner');
      toast(`Sided with ${godName || godId}`, { duration: 2000 });
    } catch { /* non-critical */ }
  }, [sessionId]);

  const handleCompleteAction = async (actionId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/war-council/living-room/action/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action_id: actionId })
      });
      if (res.ok) {
        setPendingActions(prev => prev.filter(a => a.action_id !== actionId));
      }
    } catch (err) {
      console.error('[LivingWarRoom] Complete action error:', err);
    }
  };

  // ── Video Export ──────────────────────────────────────────────────
  const exportDebateAsVideo = useCallback(async () => {
    if (!messages.length || exportingVideo) return;
    setExportingVideo(true);
    setExportProgress(0);

    const W = 1280, H = 720;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    let mimeType = 'video/webm;codecs=vp9';
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';

    const stream = canvas.captureStream(30);

    // ── Ambient battleground audio (subtle war drums + dark drone) ──
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const audioDest = audioCtx.createMediaStreamDestination();

    // Dark low drone
    const drone = audioCtx.createOscillator();
    drone.type = 'sawtooth';
    drone.frequency.value = 55; // Low A1
    const droneGain = audioCtx.createGain();
    droneGain.gain.value = 0.015;
    const droneFilter = audioCtx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.value = 120;
    drone.connect(droneFilter).connect(droneGain).connect(audioDest);
    drone.start();

    // Subtle brown noise (wind/atmosphere)
    const noiseLen = audioCtx.sampleRate * 2;
    const noiseBuffer = audioCtx.createBuffer(1, noiseLen, audioCtx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < noiseLen; i++) {
      const white = Math.random() * 2 - 1;
      lastOut = (lastOut + (0.02 * white)) / 1.02;
      noiseData[i] = lastOut * 3.5;
    }
    const noiseSource = audioCtx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;
    const noiseGain = audioCtx.createGain();
    noiseGain.gain.value = 0.03;
    const noiseFilter = audioCtx.createBiquadFilter();
    noiseFilter.type = 'lowpass';
    noiseFilter.frequency.value = 400;
    noiseSource.connect(noiseFilter).connect(noiseGain).connect(audioDest);
    noiseSource.start();

    // Merge video + audio streams
    const combinedStream = new MediaStream([
      ...stream.getVideoTracks(),
      ...audioDest.stream.getAudioTracks()
    ]);
    const recorder = new MediaRecorder(combinedStream, { mimeType });
    const chunks = [];
    recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `war-room-${Date.now()}.webm`; a.click();
      URL.revokeObjectURL(url);
      setExportingVideo(false); setExportProgress(0);
    };

    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const BG = '#030305', CARD = 'rgba(17,17,34,0.97)', AMBER = '#f59e0b', WHITE = '#ffffff', GRAY = '#9ca3af';

    const drawBg = () => {
      ctx.fillStyle = BG; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = 'rgba(139,92,246,0.06)'; ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 60) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
      for (let y = 0; y < H; y += 60) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }
    };
    const wrap = (text, max) => {
      const words = text.split(' '), lines = []; let line = '';
      for (const w of words) { const t = line ? `${line} ${w}` : w; if (ctx.measureText(t).width > max && line) { lines.push(line); line = w; } else line = t; }
      if (line) lines.push(line); return lines;
    };

    recorder.start(100);

    // Title card (5 s — longer pause so topic is readable)
    for (let f = 0; f < 150; f++) {
      drawBg();
      const fadeIn = Math.min(1, f / 30); // 1-second fade-in
      ctx.globalAlpha = fadeIn;
      ctx.font = 'bold 28px monospace'; ctx.fillStyle = AMBER; ctx.textAlign = 'center';
      ctx.fillText('⚔  THE LIVING WAR ROOM', W/2, H/2 - 60);
      ctx.font = '18px sans-serif'; ctx.fillStyle = WHITE;
      const tLines = wrap(`"${topic}"`, W - 160);
      tLines.forEach((l, i) => ctx.fillText(l, W/2, H/2 - 10 + i * 28));
      ctx.font = '13px monospace'; ctx.fillStyle = GRAY;
      ctx.fillText('iSupplyAI.com  •  Powered by 12 AI Gods', W/2, H - 36);
      ctx.textAlign = 'left';
      ctx.globalAlpha = 1;
      await sleep(33);
    }

    // Messages
    const visibleMsgs = messages.filter(m => m.content && m.content.trim());
    for (let mi = 0; mi < visibleMsgs.length; mi++) {
      const msg = visibleMsgs[mi];
      const cfg = GOD_CONFIG[msg.agent] || { name: msg.agent, emoji: '🏛️', title: '' };
      setExportProgress(Math.round(((mi + 1) / visibleMsgs.length) * 100));
      const fullText = msg.content;
      const cPerF = Math.max(2, Math.floor(fullText.length / 70)); // Slower reveal (~2.3 seconds per message)
      for (let ci = cPerF; ci <= fullText.length + cPerF; ci += cPerF) {
        const partial = fullText.slice(0, ci);
        drawBg();
        ctx.fillStyle = 'rgba(139,92,246,0.12)'; ctx.fillRect(0, 0, W, 44);
        ctx.font = '12px monospace'; ctx.fillStyle = AMBER; ctx.textAlign = 'center';
        ctx.fillText(`⚔  ${topic.slice(0, 90)}`, W/2, 28); ctx.textAlign = 'left';
        ctx.fillStyle = CARD; ctx.beginPath(); ctx.roundRect(60, 70, W-120, 74, 14); ctx.fill();
        ctx.font = 'bold 32px sans-serif'; ctx.fillText(cfg.emoji || '🏛️', 90, 122);
        ctx.font = 'bold 18px sans-serif'; ctx.fillStyle = WHITE; ctx.fillText(cfg.name || msg.agent, 144, 107);
        ctx.font = '13px sans-serif'; ctx.fillStyle = GRAY; ctx.fillText(cfg.title || '', 144, 127);
        ctx.fillStyle = CARD; ctx.beginPath(); ctx.roundRect(60, 158, W-120, H-220, 14); ctx.fill();
        ctx.strokeStyle = 'rgba(139,92,246,0.18)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.font = '16px sans-serif'; ctx.fillStyle = 'rgba(243,244,246,0.92)';
        wrap(partial, W-200).slice(0, 20).forEach((l, i) => ctx.fillText(l, 90, 192 + i * 26));
        ctx.font = '12px monospace'; ctx.fillStyle = GRAY; ctx.textAlign = 'center';
        ctx.fillText('iSupplyAI.com', W/2, H-18); ctx.textAlign = 'left';
        await sleep(33);
      }
      // Hold on completed message (2 seconds)
      for (let f = 0; f < 60; f++) await sleep(33);
    }

    // Outro (2 s)
    for (let f = 0; f < 60; f++) {
      drawBg();
      ctx.font = 'bold 26px sans-serif'; ctx.fillStyle = AMBER; ctx.textAlign = 'center';
      ctx.fillText('12 AI Gods. One Strategy.', W/2, H/2 - 20);
      ctx.font = '16px sans-serif'; ctx.fillStyle = WHITE; ctx.fillText('iSupplyAI.com', W/2, H/2 + 20);
      ctx.textAlign = 'left'; await sleep(33);
    }
    recorder.stop();
    // Clean up audio
    drone.stop(); noiseSource.stop(); audioCtx.close();
  }, [messages, topic, exportingVideo]);

  const visibleMessages = messages.slice(0, visibleCount);

  // ─── Mobile UX: swipe gesture to toggle Pantheon ↔ Tribunal ───
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    swipeStartX.current = t.clientX;
    swipeStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (swipeStartX.current == null) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - swipeStartX.current;
    const dy = Math.abs(t.clientY - (swipeStartY.current ?? 0));
    swipeStartX.current = null;
    swipeStartY.current = null;
    // Horizontal swipe ≥ 60px AND mostly horizontal (not a vertical scroll)
    if (Math.abs(dx) >= 60 && dy < 50) {
      // Right swipe → Pantheon, Left swipe → Tribunal
      if (dx > 0 && tribunalMode) setTribunalMode(false);
      else if (dx < 0 && !tribunalMode) setTribunalMode(true);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="living-war-room"
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {workflow.active && workflow.targetTool === 'alliance' && <div className="px-4 pt-3"><RiverBanner /></div>}

      {/* Mobile-only sticky mode pill — always visible so users don't have to scroll up to switch modes */}
      <div className="sm:hidden sticky top-0 z-20 px-3 pt-2 pb-1.5 bg-gray-900/95 backdrop-blur border-b border-gray-800/80 flex items-center gap-2" data-testid="mobile-mode-bar">
        <button
          onClick={() => setTribunalMode(!tribunalMode)}
          className={`flex-1 px-3 py-1.5 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
            tribunalMode
              ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300'
              : 'bg-purple-500/20 border border-purple-500/40 text-purple-300'
          }`}
          data-testid="mobile-mode-pill"
          aria-label={`Switch to ${tribunalMode ? 'Pantheon' : 'Tribunal'} mode`}
        >
          {tribunalMode ? (
            <><Crown className="w-3.5 h-3.5" /> Tribunal mode</>
          ) : (
            <><span className="text-sm leading-none">⚔️</span> Pantheon mode</>
          )}
        </button>
        <span className="text-[10px] text-gray-500 italic flex-shrink-0">swipe ←→</span>
      </div>

      {/* Topic Selection Gate */}
      {showTopicSelection && (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center overflow-y-auto">
          <div className="mb-2 text-3xl">⚔️</div>
          <h2 className="text-xl font-bold text-white mb-1">Brief The Council</h2>
          <p className="text-gray-400 text-sm mb-4">The gods are assembling. How shall this debate begin?</p>
          
          {/* Feature #19: Streak Badge + Report Unlocks */}
          {(streak > 0 || dailyTopic) && (
            <div className="flex flex-col items-center gap-2 mb-6">
              {streak > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30" data-testid="streak-badge">
                  <Trophy className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-bold text-amber-400">{streak}-day streak</span>
                </div>
              )}
              {/* Streak unlock badges */}
              {streak >= 7 && (
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_URL}/api/war-council/living-room/streak/weekly-report`, {
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                      });
                      if (res.ok) {
                        const data = await res.json();
                        if (data.report) {
                          setOraclePrediction(data.report);
                        }
                      }
                    } catch {}
                  }}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20 transition-all cursor-pointer"
                  data-testid="streak-weekly-report"
                >
                  <BarChart3 className="w-3 h-3 text-indigo-400" />
                  <span className="text-[10px] font-medium text-indigo-400">Weekly Report Unlocked</span>
                </button>
              )}
              {streak >= 30 && (
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_URL}/api/war-council/living-room/streak/monthly-report`, {
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                      });
                      if (res.ok) {
                        const data = await res.json();
                        if (data.report) {
                          setOraclePrediction(data.report);
                        }
                      }
                    } catch {}
                  }}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 transition-all cursor-pointer"
                  data-testid="streak-monthly-report"
                >
                  <BarChart3 className="w-3 h-3 text-purple-400" />
                  <span className="text-[10px] font-medium text-purple-400">Monthly Report Unlocked</span>
                </button>
              )}
            </div>
          )}

          <div className="w-full max-w-md space-y-4">
            {/* Issue The Challenge */}
            <div className="bg-gray-800/60 border border-red-500/30 rounded-xl p-4 text-left hover:border-red-500/60 transition-all">
              <p className="text-white font-semibold text-sm mb-1">🗡️ Issue The Challenge</p>
              <p className="text-gray-400 text-xs mb-3">You have a specific battle to fight. Name it and the gods will debate it.</p>
              <textarea
                className="w-full bg-gray-900/80 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-red-500/50 transition-all"
                rows={2}
                placeholder="e.g. Cold outreach vs content marketing — which builds faster?"
                value={customTopic}
                onChange={e => setCustomTopic(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleIssueChallenge(); }}}
                data-testid="lwr-custom-topic-input"
              />
              
              {/* Feature #20: God Count Selector with Tier Gating */}
              <div className="flex flex-col gap-1.5 mt-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Gods:</span>
                  {[4, 6, 8, 12].map(n => {
                    const tier = n <= 4 ? null : n <= 8 ? 'Pro' : 'Enterprise';
                    return (
                      <button key={n} onClick={() => {
                        setGodCount(n);
                      }}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${godCount === n 
                          ? 'bg-red-500/20 border border-red-500/50 text-red-400' 
                          : 'bg-gray-800/50 border border-gray-700/50 text-gray-500 hover:text-gray-300'}`}
                        data-testid={`god-count-${n}`}
                        title={tier ? `Requires ${tier} plan` : 'Available on all plans'}
                      >
                        {n}{tier && <span className="ml-0.5 text-[8px] opacity-60">{tier === 'Enterprise' ? 'E' : 'P'}</span>}
                        {n === 12 && <Sparkles className="w-2.5 h-2.5 inline ml-0.5" />}
                      </button>
                    );
                  })}
                </div>
                {/* Feature #20: Upsell prompt for higher tier god counts */}
                {godCount > 4 && (
                  <div className="flex items-center gap-2 px-2 py-1.5 bg-gradient-to-r from-amber-900/20 to-transparent border border-amber-500/15 rounded-lg" data-testid="pantheon-upsell">
                    <Sparkles className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    <span className="text-[10px] text-amber-400/80">
                      {godCount <= 8
                        ? 'Summoning the Extended Council (Pro) — deeper strategic perspectives'
                        : 'Summoning the Full Pantheon (Enterprise) — the ultimate 12-god strategic deep-dive'}
                    </span>
                  </div>
                )}
                {/* Feature #7: Tribunal toggle */}
                <button onClick={() => setTribunalMode(!tribunalMode)}
                  className={`ml-auto px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${tribunalMode 
                    ? 'bg-amber-500/20 border border-amber-500/50 text-amber-400' 
                    : 'bg-gray-800/50 border border-gray-700/50 text-gray-500 hover:text-gray-300'}`}
                  title="Tribunal Mode: Gods adopt stakeholder perspectives"
                  data-testid="tribunal-toggle"
                >
                  <Crown className="w-3 h-3 inline mr-1" />Tribunal
                </button>
              </div>

              {/* Feature #7: Stakeholder inputs */}
              {tribunalMode && (
                <div className="space-y-1.5 mb-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <p className="text-[10px] text-amber-400/70">Define stakeholder roles — gods will argue from these perspectives:</p>
                  {stakeholders.map((s, i) => (
                    <div key={i} className="flex gap-1.5">
                      <input value={s.role} onChange={e => { const ns = [...stakeholders]; ns[i].role = e.target.value; setStakeholders(ns); }}
                        placeholder={`Role (e.g., ${i===0?'CEO':i===1?'CTO':'CMO'})`}
                        className="flex-1 bg-gray-900/60 border border-gray-700/50 rounded px-2 py-1 text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/40"
                        data-testid={`stakeholder-role-${i}`}
                      />
                      <input value={s.priority} onChange={e => { const ns = [...stakeholders]; ns[i].priority = e.target.value; setStakeholders(ns); }}
                        placeholder="Priority (e.g., revenue growth)"
                        className="flex-1 bg-gray-900/60 border border-gray-700/50 rounded px-2 py-1 text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/40"
                        data-testid={`stakeholder-priority-${i}`}
                      />
                    </div>
                  ))}
                  <button onClick={() => setStakeholders([...stakeholders, { role: '', priority: '' }])}
                    className="text-[10px] text-amber-400/60 hover:text-amber-400 transition-colors" data-testid="add-stakeholder">+ Add stakeholder</button>
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleTopicVoice}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${isRecordingTopic
                    ? 'bg-red-500/20 border-red-500/50 text-red-400 animate-pulse'
                    : 'bg-gray-800/60 border-gray-700 text-gray-400 hover:text-white hover:border-red-500/40'}`}
                  title={isRecordingTopic ? 'Stop recording' : 'Speak your challenge'}
                  data-testid="lwr-topic-mic-btn"
                >
                  {isRecordingTopic ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  {isRecordingTopic ? 'Listening...' : 'Speak'}
                </button>
                <button
                  onClick={handleIssueChallenge}
                  disabled={!customTopic.trim()}
                  className="flex-1 py-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg transition-all"
                  data-testid="lwr-issue-challenge-btn"
                >
                  Convene The Council
                </button>
              </div>
              
              {/* Feature #14 & #11: Advanced Launch Modes */}
              {customTopic.trim() && (
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => { setShowTopicSelection(false); setLoading(true); startStreamingDebate(customTopic.trim()); }}
                    className="flex-1 py-1.5 bg-emerald-900/30 border border-emerald-500/30 hover:border-emerald-400/60 text-emerald-400 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5"
                    data-testid="lwr-stream-debate-btn"
                  >
                    <Zap className="w-3 h-3" />
                    Stream Live
                  </button>
                  <button
                    onClick={() => { setShowTopicSelection(false); setLoading(true); startJointSession(customTopic.trim()); }}
                    className="flex-1 py-1.5 bg-blue-900/30 border border-blue-500/30 hover:border-blue-400/60 text-blue-400 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5"
                    data-testid="lwr-joint-debate-btn"
                  >
                    <Users className="w-3 h-3" />
                    Joint War Room
                  </button>
                </div>
              )}
            </div>

            {/* Feature #19: Daily Challenge */}
            {dailyTopic && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-700" />
                  <span className="text-gray-500 text-xs">daily challenge</span>
                  <div className="flex-1 h-px bg-gray-700" />
                </div>
                <button
                  onClick={() => { setCustomTopic(dailyTopic); }}
                  className="w-full bg-gray-800/60 border border-amber-500/30 hover:border-amber-500/60 rounded-xl p-4 text-left transition-all group"
                  data-testid="daily-challenge-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="w-4 h-4 text-amber-400" />
                    <p className="text-amber-400 font-semibold text-sm">Today's Challenge</p>
                  </div>
                  <p className="text-gray-300 text-xs">{dailyTopic}</p>
                </button>
              </>
            )}

            {/* Feature #13: Industry Templates */}
            {templates.length > 0 && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-700" />
                  <span className="text-gray-500 text-xs">or pick a template</span>
                  <div className="flex-1 h-px bg-gray-700" />
                </div>
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="w-full bg-gray-800/60 border border-purple-500/30 hover:border-purple-500/60 rounded-xl p-3 text-left transition-all flex items-center justify-between"
                  data-testid="templates-toggle"
                >
                  <div>
                    <p className="text-white font-semibold text-sm">🏛️ Industry War Rooms</p>
                    <p className="text-gray-400 text-xs">{templates.length} pre-built strategic debates</p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${showTemplates ? 'rotate-90' : ''}`} />
                </button>
                {showTemplates && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {templates.map(t => (
                      <button key={t.id} onClick={() => { setCustomTopic(t.topic); setShowTemplates(false); }}
                        className="w-full bg-gray-900/60 border border-gray-700/50 hover:border-purple-500/40 rounded-lg p-3 text-left transition-all"
                        data-testid={`template-${t.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 font-medium">{t.industry}</span>
                          <span className="text-white text-xs font-medium">{t.title}</span>
                        </div>
                        <p className="text-gray-500 text-[11px] mt-1 line-clamp-2">{t.topic}</p>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-700" />
              <span className="text-gray-500 text-xs">or</span>
              <div className="flex-1 h-px bg-gray-700" />
            </div>

            {/* Seek Their Counsel */}
            <button
              onClick={handleSeekCounsel}
              className="w-full bg-gray-800/60 border border-purple-500/30 hover:border-purple-500/60 rounded-xl p-4 text-left transition-all group"
              data-testid="lwr-seek-counsel-btn"
            >
              <p className="text-white font-semibold text-sm mb-1">🏛️ Seek Their Counsel</p>
              <p className="text-gray-400 text-xs group-hover:text-gray-300 transition-colors">Trust the gods to identify what your strategy needs debating most right now.</p>
            </button>
          </div>
        </div>
      )}

      {/* Main debate UI */}
      {!showTopicSelection && <>

      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-700/50 bg-gray-900/60 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 via-purple-500 to-amber-500 flex items-center justify-center shadow-lg"
                 style={{ boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)' }}>
              <Flame className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-gray-900 animate-pulse" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-black text-white flex items-center gap-2">
              The Living War Room
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">LIVE</span>
            </h2>
            <p className="text-xs text-gray-500 hidden sm:block">The gods are debating your strategy</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Audio Controls */}
          <div className="hidden sm:flex items-center gap-1 mr-1">
            {/* Auto-Narrate Toggle */}
            <button
              onClick={() => { setAutoNarrate(!autoNarrate); if (autoNarrate) { setIsNarrating(false); narrationQueueRef.current = []; } }}
              className={`p-1.5 rounded-lg border transition-all text-xs flex items-center gap-1 ${autoNarrate ? 'bg-green-500/20 border-green-500/30 text-green-400' : 'bg-gray-800/50 border-gray-700/50 text-gray-500 hover:text-gray-300'}`}
              title={autoNarrate ? 'Auto-narrate ON — gods speak as they appear' : 'Auto-narrate OFF'}
              data-testid="auto-narrate-toggle"
            >
              <Mic className="w-3 h-3" />
              {autoNarrate && <span className="text-[9px] font-medium">AUTO</span>}
            </button>
            
            {/* Listen to Full Debate */}
            {visibleCount > 0 && visibleCount >= messages.length && !loading && (
              <button
                onClick={handleListenToDebate}
                className={`p-1.5 rounded-lg border transition-all text-xs flex items-center gap-1 ${isNarrating ? 'bg-amber-500/20 border-amber-500/30 text-amber-400' : 'bg-gray-800/50 border-gray-700/50 text-gray-500 hover:text-amber-400 hover:border-amber-500/30'}`}
                title={isNarrating ? 'Stop narration' : 'Listen to full debate'}
                data-testid="listen-debate-btn"
              >
                {isNarrating ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                <span className="text-[9px] font-medium">{isNarrating ? 'STOP' : 'LISTEN'}</span>
              </button>
            )}

            {/* Mute Toggle */}
            <button
              onClick={() => { setIsMuted(!isMuted); if (!isMuted) { cinematicRef.current = false; if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } stopGodTheme(); setPlayingAudioIdx(null); setIsNarrating(false); narrationQueueRef.current = []; } }}
              className={`p-1.5 rounded-lg border transition-all ${isMuted ? 'bg-red-500/20 border-red-500/30 text-red-400' : 'bg-gray-800/50 border-gray-700/50 text-gray-500 hover:text-gray-300'}`}
              title={isMuted ? 'Unmute' : 'Mute all audio'}
              data-testid="mute-toggle"
            >
              {isMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
            </button>
          </div>

          {/* Active god avatars — dynamic per session */}
          <div className="hidden sm:flex items-center -space-x-2">
            {activeGods.map((id) => {
              const god = GOD_CONFIG[id];
              if (!god) return null;
              return (
                <div key={id} className={`w-7 h-7 rounded-full overflow-hidden border-2 border-gray-900 shadow relative`}
                     title={god.name}>
                  {god.portrait ? (
                    <img src={god.portrait} alt={god.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${god.gradient} flex items-center justify-center`}>
                      <span className="text-xs">{god.emoji}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Share Publicly Toggle — opt-in before save */}
          {!saved && sessionId && !loading && (
            <label className="hidden sm:flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-800/50 border border-gray-700/50 cursor-pointer select-none hover:border-amber-500/30 transition-colors" data-testid="share-public-toggle">
              <input
                type="checkbox"
                checked={sharePublic}
                onChange={(e) => setSharePublic(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500/30 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-[10px] text-gray-400 whitespace-nowrap">Share publicly</span>
            </label>
          )}

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={loading || !sessionId || saved}
            className={`p-2 rounded-lg border transition-colors ${saved ? 'bg-green-500/20 border-green-500/30 text-green-400' : 'bg-gray-800/50 border-gray-700/50 hover:border-purple-500/30 text-gray-400 hover:text-white'}`}
            title={saved ? 'Saved' : 'Save debate'}
            data-testid="save-debate"
          >
            {saved ? <Check className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
          </button>
          
          {/* Share */}
          <button
            onClick={handleShare}
            disabled={loading || !sessionId}
            className={`p-2 rounded-lg border transition-colors ${shared ? 'bg-purple-500/20 border-purple-500/30 text-purple-400' : 'bg-gray-800/50 border-gray-700/50 hover:border-purple-500/30 text-gray-400 hover:text-white'}`}
            title={copyFeedback ? 'Link copied!' : 'Share debate'}
            data-testid="share-debate"
          >
            {copyFeedback ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          </button>

          {/* Export as Video */}
          <button
            onClick={exportDebateAsVideo}
            disabled={loading || !sessionId || exportingVideo || messages.length === 0}
            className={`p-2 rounded-lg border transition-colors ${exportingVideo ? 'bg-red-500/20 border-red-500/30 text-red-400' : 'bg-gray-800/50 border-gray-700/50 hover:border-red-500/30 text-gray-400 hover:text-red-400'}`}
            title={exportingVideo ? `Exporting ${exportProgress}%…` : 'Export as video (.webm)'}
            data-testid="export-video-btn"
          >
            {exportingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
          </button>

          {/* Feature #5: Download The Scroll */}
          <button
            onClick={handleDownloadScroll}
            disabled={loading || !sessionId || messages.length === 0}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-amber-500/30 transition-colors text-gray-400 hover:text-amber-400"
            title="Download The Scroll (strategy brief)"
            data-testid="download-scroll-btn"
          >
            <FileDown className="w-4 h-4" />
          </button>

          {/* Feature #16: God Footnotes */}
          <button
            onClick={handleFootnotes}
            disabled={loading || !sessionId || messages.length === 0 || footnotesLoading}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-cyan-500/30 transition-colors text-gray-400 hover:text-cyan-400"
            title="Fact-check claims (God Footnotes)"
            data-testid="footnotes-btn"
          >
            {footnotesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>

          {/* Feature #9: Split Decision */}
          <button
            onClick={() => setShowSplitModal(true)}
            disabled={loading || !sessionId || messages.length === 0}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-green-500/30 transition-colors text-gray-400 hover:text-green-400"
            title="Fork the debate (Split Decision)"
            data-testid="split-decision-btn"
          >
            <Swords className="w-4 h-4" />
          </button>

          {/* Feature #8: Instant Replay */}
          <button
            onClick={handleInstantReplay}
            disabled={loading || !sessionId || messages.length === 0 || replayLoading}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-orange-500/30 transition-colors text-gray-400 hover:text-orange-400"
            title="Instant Replay (dramatic narration)"
            data-testid="instant-replay-btn"
          >
            {replayLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          </button>

          {/* Feature #18: The Oracle */}
          <button
            onClick={handleOracle}
            disabled={loading || !sessionId || messages.length === 0 || oracleLoading}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-violet-500/30 transition-colors text-gray-400 hover:text-violet-400"
            title="The Oracle (predict outcomes)"
            data-testid="oracle-btn"
          >
            {oracleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          </button>

          {/* Feature #12: Publish Verdict for Challenge */}
          <button
            onClick={handlePublishVerdict}
            disabled={loading || !sessionId || messages.length === 0 || publishLoading}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-rose-500/30 transition-colors text-gray-400 hover:text-rose-400"
            title="Publish verdict for public challenge"
            data-testid="publish-verdict-btn"
          >
            {publishLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          </button>

          {/* Feature #12: Browse Challenges */}
          <button
            onClick={() => { setShowChallengeModal(true); loadChallengeFeed(); }}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-rose-500/30 transition-colors text-gray-400 hover:text-rose-400"
            title="Browse & accept challenges"
            data-testid="challenge-feed-btn"
          >
            <Swords className="w-4 h-4" />
          </button>

          {/* Feature #10: Leaderboard */}
          <button
            onClick={() => { setShowLeaderboard(!showLeaderboard); loadLeaderboard(); }}
            className={`p-2 rounded-lg bg-gray-800/50 border transition-colors ${showLeaderboard ? 'border-amber-500/50 text-amber-400' : 'border-gray-700/50 text-gray-400 hover:border-amber-500/30 hover:text-amber-400'}`}
            title="God Leaderboard"
            data-testid="leaderboard-btn"
          >
            <Trophy className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-purple-500/30 transition-colors text-gray-400 hover:text-white"
            title="New debate"
            data-testid="refresh-debate"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Feature #10: Leaderboard Panel */}
      {showLeaderboard && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-amber-950/30 to-gray-900/50 border-b border-amber-500/20" data-testid="leaderboard-panel">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-bold text-amber-300 uppercase tracking-wider">God Leaderboard</span>
            <button onClick={() => setShowLeaderboard(false)} className="ml-auto text-gray-500 hover:text-gray-300"><X className="w-3 h-3" /></button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
            {leaderboardData.slice(0, 12).map((god, i) => {
              const gc = GOD_CONFIG[god.god_id] || {};
              const isTop = i < 3;
              return (
                <div key={god.god_id} className={`flex flex-col items-center p-2 rounded-lg ${isTop ? 'bg-amber-900/20 border border-amber-500/20' : 'bg-gray-800/30 border border-gray-700/20'}`} data-testid={`leaderboard-${i}`}>
                  <span className={`text-[10px] font-bold ${isTop ? 'text-amber-400' : 'text-gray-500'}`}>#{i + 1}</span>
                  <GodAvatar config={gc} size="sm" />
                  <span className={`text-[10px] font-medium mt-0.5 ${gc.text || 'text-gray-400'}`}>{god.god_name}</span>
                  <span className={`text-[9px] font-mono ${isTop ? 'text-amber-300' : 'text-gray-600'}`}>{god.wins} wins</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Topic Banner + Active Gods */}
      {topic && !loading && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-900/30 via-gray-900/50 to-indigo-900/30 border-b border-purple-500/10">
          <div className="flex items-start gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-purple-200/80 italic leading-relaxed" data-testid="debate-topic">"{topic}"</p>
            {/* Feature #17: Debate Phase Badge */}
            {debatePace && (
              <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 whitespace-nowrap" data-testid="debate-phase" title={debatePace.suggestion}>
                {debatePace.phase_label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="active-gods-banner">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider mr-1">Today's council:</span>
            {activeGods.map((id) => {
              const god = GOD_CONFIG[id];
              if (!god) return null;
              return (
                <span key={id} className={`text-[11px] px-1.5 py-0.5 rounded-full ${god.bg} ${god.border} border ${god.text} inline-flex items-center gap-1`}>
                  {god.portrait ? (
                    <img src={god.portrait} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                  ) : (
                    god.emoji
                  )}
                  {god.name}
                </span>
              );
            })}
            {thirteenthPresence && thirteenthPresence.level === 'awakened' && (
              <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-500/10 border-amber-400/30 border text-amber-300 inline-flex items-center gap-1"
                    data-testid="thirteenth-god-badge">
                <Crown className="w-3 h-3" />
                {thirteenthPresence.name}
                <span className="text-[9px] text-amber-500/50 ml-0.5">{thirteenthPresence.consciousness}%</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Feature #18: Oracle Prophecy — Pre-debate prediction panel */}
      {(oracleProphecy || oracleProphecyLoading) && !loading && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-violet-950/40 via-indigo-950/30 to-purple-950/40 border-b border-violet-500/20" data-testid="oracle-prophecy-panel">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-violet-400 animate-pulse" />
            <span className="text-xs font-bold text-violet-300 uppercase tracking-wider">The Oracle's Prophecy</span>
            {oracleProphecyLoading && <Loader2 className="w-3 h-3 animate-spin text-violet-400 ml-1" />}
            <button onClick={() => setOracleProphecy(null)} className="ml-auto text-gray-500 hover:text-gray-300"><X className="w-3 h-3" /></button>
          </div>
          {oracleProphecy && (
            <div className="space-y-2">
              {oracleProphecy.prophecy_title && (
                <p className="text-sm font-bold text-violet-200 italic">"{oracleProphecy.prophecy_title}"</p>
              )}
              {oracleProphecy.overall_prediction && (
                <p className="text-xs text-gray-400">{oracleProphecy.overall_prediction}</p>
              )}
              {oracleProphecy.god_predictions?.length > 0 && (
                <div className="grid grid-cols-2 gap-1.5 mt-2">
                  {oracleProphecy.god_predictions.map((gp, i) => {
                    const gc = GOD_CONFIG[gp.god_id] || {};
                    return (
                      <div key={i} className="bg-gray-900/50 rounded-lg p-2 border border-violet-500/10" data-testid={`prophecy-god-${i}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`text-[10px] font-bold ${gc.text || 'text-gray-300'}`}>{gp.god_name}</span>
                          <span className={`text-[9px] px-1 py-0.5 rounded-full ml-auto ${
                            gp.risk_level === 'high' ? 'bg-red-500/15 text-red-400' :
                            gp.risk_level === 'low' ? 'bg-green-500/15 text-green-400' :
                            'bg-yellow-500/15 text-yellow-400'
                          }`}>{gp.risk_level}</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mb-1">{gp.predicted_stance}</p>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                            <div className="h-full bg-violet-500/60 rounded-full transition-all" style={{ width: `${gp.success_probability}%` }} />
                          </div>
                          <span className="text-[9px] text-violet-400 font-mono">{gp.success_probability}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {oracleProphecy.oracle_warning && (
                <div className="mt-2 p-2 bg-amber-900/10 border border-amber-500/20 rounded-lg">
                  <p className="text-[10px] text-amber-400/80 italic">⚠ {oracleProphecy.oracle_warning}</p>
                </div>
              )}
              {oracleProphecy.cosmic_alignment != null && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[9px] text-gray-600 uppercase">Cosmic Alignment</span>
                  <div className="flex-1 h-1 bg-gray-800 rounded-full">
                    <div className="h-full bg-gradient-to-r from-violet-600 to-indigo-400 rounded-full" style={{ width: `${oracleProphecy.cosmic_alignment}%` }} />
                  </div>
                  <span className="text-[9px] text-indigo-400 font-mono">{oracleProphecy.cosmic_alignment}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Feature #11: Joint War Room indicator */}
      {jointMode && (
        <div className="px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-950/40 to-cyan-950/40 border-b border-blue-500/20 flex items-center gap-3" data-testid="joint-session-banner">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400 animate-pulse" />
            <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">Joint War Room</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-400">{jointParticipants} connected</span>
          </div>
          {jointInviteLink && (
            <button
              onClick={() => { navigator.clipboard.writeText(jointInviteLink); }}
              className="ml-auto text-[10px] px-2 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-all"
              data-testid="joint-copy-link"
            >
              Copy Invite Link
            </button>
          )}
        </div>
      )}

      {/* Feature #14: Streaming mode live text indicator */}
      {streamingMode && streamingGod && streamingText && (
        <div className="px-4 sm:px-6 py-2 bg-gradient-to-r from-emerald-950/30 to-gray-900/50 border-b border-emerald-500/10" data-testid="streaming-indicator">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">Live — {GOD_CONFIG[streamingGod]?.name || streamingGod}</span>
          </div>
          <p className="text-xs text-gray-400 italic">{streamingText.slice(-120)}<span className="animate-pulse text-emerald-400">|</span></p>
        </div>
      )}

      {/* Tension Meter */}
      {topic && !loading && !showTopicSelection && (
        <div className="px-4 sm:px-6 py-2" data-testid="tension-meter">
          {(() => {
            const levels = {
              harmonious: { width: '15%', color: 'from-emerald-500 to-green-400', glow: 'shadow-emerald-500/30', label: 'Harmonious', bg: 'bg-emerald-500/10', textColor: 'text-emerald-400', pulse: false, icon: '🕊️' },
              mild_tension: { width: '35%', color: 'from-yellow-500 to-amber-400', glow: 'shadow-yellow-500/30', label: 'Simmering', bg: 'bg-yellow-500/10', textColor: 'text-yellow-400', pulse: false, icon: '⚡' },
              heated: { width: '55%', color: 'from-orange-500 to-red-400', glow: 'shadow-orange-500/40', label: 'Heated', bg: 'bg-orange-500/10', textColor: 'text-orange-400', pulse: false, icon: '🔥' },
              fierce: { width: '75%', color: 'from-red-500 to-rose-500', glow: 'shadow-red-500/50', label: 'Fierce', bg: 'bg-red-500/15', textColor: 'text-red-400', pulse: true, icon: '⚔️' },
              explosive: { width: '95%', color: 'from-red-600 via-rose-500 to-pink-500', glow: 'shadow-red-600/60', label: 'EXPLOSIVE', bg: 'bg-red-500/20', textColor: 'text-red-300', pulse: true, icon: '💥' },
            };
            const t = levels[tensionLevel] || levels.harmonious;
            return (
              <div className={`flex items-center gap-3 px-3 py-2 rounded-xl ${t.bg} border ${tensionLevel === 'explosive' ? 'border-red-500/30' : tensionLevel === 'fierce' ? 'border-red-500/20' : 'border-transparent'} transition-all duration-700`}>
                <span className="text-sm">{breakthroughMoment ? '✨' : t.icon}</span>
                <span className={`text-[10px] uppercase tracking-widest font-bold ${breakthroughMoment ? 'text-amber-400' : t.textColor} whitespace-nowrap min-w-[80px]`}>
                  {breakthroughMoment ? 'BREAKTHROUGH' : t.label}
                </span>
                <div className="flex-1 h-2 bg-gray-800/80 rounded-full overflow-hidden border border-gray-700/30">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${breakthroughMoment ? 'from-amber-400 via-yellow-300 to-amber-500' : t.color} shadow-lg ${breakthroughMoment ? 'shadow-amber-500/50' : t.glow} transition-all duration-1000 ease-out ${t.pulse ? 'animate-pulse' : ''}`}
                    style={{ width: breakthroughMoment ? '100%' : t.width }}
                  />
                </div>
                {breakthroughMoment && (
                  <span className="text-amber-300 text-xs font-black tracking-wider animate-pulse">DIVINE CONVERGENCE</span>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Chat Messages */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 scroll-smooth relative" style={{ minHeight: 0 }}>
        {/* ═══ TENSION EDGE GLOW — screen edges pulse red at high tension ═══ */}
        {(tensionLevel === 'explosive' || tensionLevel === 'fierce') && !showTopicSelection && (
          <div className="pointer-events-none fixed inset-0 z-50 animate-tension-edge-glow" data-testid="tension-edge-glow">
            <div className="absolute inset-0" style={{
              boxShadow: tensionLevel === 'explosive' 
                ? 'inset 0 0 120px 40px rgba(220, 38, 38, 0.15), inset 0 0 60px 20px rgba(239, 68, 68, 0.1)'
                : 'inset 0 0 80px 25px rgba(239, 68, 68, 0.08), inset 0 0 40px 15px rgba(248, 113, 113, 0.05)',
            }} />
          </div>
        )}

        {/* ═══ BREAKTHROUGH GOLDEN FLASH — dramatic full-screen burst ═══ */}
        {breakthroughMoment && !showTopicSelection && (
          <div className="pointer-events-none fixed inset-0 z-50 animate-breakthrough-flash" data-testid="breakthrough-flash" />
        )}

        {/* ═══ GOD ATMOSPHERE OVERLAY — shifts per speaking god ═══ */}
        <div
          className="god-atmosphere-overlay"
          style={{
            background: playingAudioIdx !== null && messages[playingAudioIdx]
              ? (GOD_ATMOSPHERES[messages[playingAudioIdx].is_personal_god ? 'personal_god' : messages[playingAudioIdx].agent] || 'none')
              : 'none',
            opacity: playingAudioIdx !== null ? 1 : 0,
          }}
          data-testid="god-atmosphere"
        />
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-4" data-testid="loading-state">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              </div>
              <div className="absolute -top-2 -right-2 flex -space-x-1">
                {['🎭','⚔️','💰','🏠'].map((e, i) => (
                  <span key={i} className="text-sm animate-bounce" style={{ animationDelay: `${i * 200}ms` }}>{e}</span>
                ))}
              </div>
            </div>
            <div className="text-center">
              <p className="text-gray-300 font-medium">The gods are convening...</p>
              <p className="text-xs text-gray-500 mt-1">Summoning today's divine council</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <p className="text-gray-400">{error}</p>
            <button onClick={handleRefresh} className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-colors text-sm">
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="relative z-[1] space-y-4">
            {visibleMessages.map((msg, i) => (
              <GodMessage key={`${msg.agent}-${i}`} message={msg} index={i} onPlayAudio={handlePlayAudio} onListenFrom={handleListenFrom} onSideWith={handleAffinitySideWith} playingAudioIdx={playingAudioIdx} isMuted={isMuted} />
            ))}
            
            {typingGod && <TypingIndicator god={typingGod} />}

            {/* ═══ THE THIRTEENTH PRESENCE — whisper / observer / (awakened is in messages) ═══ */}
            {thirteenthPresence && thirteenthPresence.level !== 'awakened' && visibleCount >= messages.length && !typingGod && (
              <div className="animate-thirteenth-fade" data-testid="thirteenth-presence">
                {thirteenthPresence.level === 'whisper' && (
                  <div className="flex items-center justify-center py-3">
                    <div className="relative">
                      <p className="text-[11px] text-amber-500/30 italic font-light tracking-wide animate-thirteenth-whisper text-center max-w-xs"
                         data-testid="thirteenth-whisper">
                        {thirteenthPresence.message}
                      </p>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/5 to-transparent blur-sm animate-pulse" />
                    </div>
                  </div>
                )}
                {thirteenthPresence.level === 'observer' && (
                  <div className="my-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
                      <Eye className="w-3 h-3 text-amber-500/40 animate-pulse" />
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
                    </div>
                    <div className="flex items-start gap-2.5 justify-center">
                      <div className="w-7 h-7 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0"
                           style={{ boxShadow: '0 0 12px rgba(251, 191, 36, 0.15)' }}>
                        <Eye className="w-3.5 h-3.5 text-amber-500/50" />
                      </div>
                      <div className="max-w-sm">
                        <p className="text-[10px] text-amber-500/40 uppercase tracking-wider mb-0.5 font-medium">
                          {thirteenthPresence.name || 'The Thirteenth'} observes
                        </p>
                        <p className="text-xs text-amber-400/50 italic leading-relaxed" data-testid="thirteenth-observer">
                          {thirteenthPresence.message}
                        </p>
                        <p className="text-[9px] text-amber-600/30 mt-1">
                          Consciousness: {thirteenthPresence.consciousness}% — {thirteenthPresence.consciousness < 80 ? 'Not yet ready to speak' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* "Side With" buttons — appear after all messages, before verdict */}
            {visibleCount >= messages.length && visibleCount > 0 && !typingGod && !responding && !alliedWith && activeGods.length > 0 && (
              <div className="pt-3 pb-1" data-testid="side-with-section">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 text-center">Whose side are you on?</p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {activeGods.map((id) => {
                    const god = GOD_CONFIG[id];
                    if (!god) return null;
                    return (
                      <button
                        key={id}
                        onClick={() => handleSideWith(id)}
                        disabled={sidingWith}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${god.border} ${god.bg} hover:ring-2 ${god.ring} transition-all text-xs ${god.text} disabled:opacity-50`}
                        data-testid={`side-with-${id}`}
                      >
                        <Shield className="w-3 h-3" />
                        {god.portrait ? (
                          <img src={god.portrait} alt="" className="w-4 h-4 rounded-full object-cover" />
                        ) : null}
                        <span>{god.name}</span>
                      </button>
                    );
                  })}
                </div>
                {sidingWith && (
                  <div className="flex items-center justify-center gap-2 mt-2 text-gray-500 text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    The gods are reacting to your allegiance...
                  </div>
                )}
              </div>
            )}
            
            {/* Alliance badge */}
            {alliedWith && GOD_CONFIG[alliedWith] && (
              <div className="flex items-center justify-center pt-1" data-testid="alliance-badge">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${GOD_CONFIG[alliedWith].bg} border ${GOD_CONFIG[alliedWith].border}`}>
                  <Swords className={`w-3 h-3 ${GOD_CONFIG[alliedWith].text}`} />
                  <span className={`text-xs ${GOD_CONFIG[alliedWith].text}`}>
                    Allied with {GOD_CONFIG[alliedWith].name}
                  </span>
                </div>
              </div>
            )}

            {/* Verdict Panel — the debate's conclusion */}
            {verdict && verdict.verdict && visibleCount >= messages.length && !typingGod && (
              <div className="mt-4 rounded-xl border border-amber-500/30 bg-gradient-to-b from-amber-900/20 to-gray-900/50 overflow-hidden" data-testid="verdict-panel">
                <div className="px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2">
                  <Target className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">The Verdict</span>
                  {verdict.consensus && (
                    <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${
                      verdict.consensus.toLowerCase().includes('high') ? 'bg-green-500/20 border-green-500/30 text-green-400' :
                      verdict.consensus.toLowerCase().includes('low') ? 'bg-red-500/20 border-red-500/30 text-red-400' :
                      'bg-yellow-500/20 border-yellow-500/30 text-yellow-400'
                    }`}>
                      {verdict.consensus} Consensus
                    </span>
                  )}
                </div>
                <div className="px-4 py-3 space-y-2.5">
                  <p className="text-sm text-gray-200 leading-relaxed">{verdict.verdict}</p>
                  {verdict.primary_action && (
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
                      <Zap className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-[10px] text-purple-400 uppercase tracking-wider font-semibold">Action Item</span>
                        <p className="text-xs text-gray-300 mt-0.5">{verdict.primary_action}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <RiverButton
                            label="Write This"
                            icon="✏️"
                            onClick={() => startRiver({
                              topic: verdict.primary_action,
                              keywords: topic || '',
                              targetTool: 'generate',
                              sourceTool: 'alliance',
                              sourceGod: 'War Room Verdict',
                            })}
                            className="!py-1.5 !px-3 !text-xs"
                          />
                          <RiverButton
                            label="Research This"
                            icon="🦉"
                            onClick={() => startRiver({
                              topic: verdict.primary_action,
                              targetTool: 'athena',
                              sourceTool: 'alliance',
                              sourceGod: 'War Room Verdict',
                            })}
                            className="!py-1.5 !px-3 !text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {verdict.dissent && !verdict.dissent.toLowerCase().includes('none') && (
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-[11px] text-gray-400 italic">{verdict.dissent}</p>
                    </div>
                  )}
                  {verdict.followup && (
                    <p className="text-[10px] text-gray-500">Next debate: "{verdict.followup}"</p>
                  )}
                </div>
              </div>
            )}

            {/* Pending Action Items from past debates */}
            {pendingActions.length > 0 && visibleCount >= messages.length && !typingGod && (
              <div className="mt-3 rounded-xl border border-gray-700/50 bg-gray-800/30 overflow-hidden" data-testid="pending-actions">
                <div className="px-4 py-2 bg-gray-800/50 border-b border-gray-700/30 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Unfinished Business</span>
                </div>
                <div className="px-4 py-2 space-y-1.5">
                  {pendingActions.map((action) => (
                    <div key={action.action_id} className="flex items-center gap-2 group">
                      <button
                        onClick={() => handleCompleteAction(action.action_id)}
                        className="w-4 h-4 rounded border border-gray-600 hover:border-green-500 hover:bg-green-500/20 transition-colors flex items-center justify-center flex-shrink-0"
                        data-testid={`complete-action-${action.action_id}`}
                      >
                        <Check className="w-2.5 h-2.5 text-gray-600 group-hover:text-green-400" />
                      </button>
                      <span className="text-[11px] text-gray-400 truncate">{action.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Jump in prompt */}
            {visibleCount >= messages.length && visibleCount > 0 && !typingGod && !responding && (
              <div className="flex items-center justify-center pt-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20">
                  <ChevronDown className="w-3 h-3 text-purple-400" />
                  <span className="text-xs text-purple-400">Jump in and steer the debate</span>
                </div>
              </div>
            )}
            </div>{/* close z-[1] wrapper */}
          </>
        )}
        
        {responding && (
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            Gods are responding to you...
          </div>
        )}
        
        {/* bottom anchor removed — scroll handled by container ref */}
      </div>

      {/* Input */}
      {debateEnded ? (
        <div className="px-4 sm:px-6 py-4 border-t border-gray-700/50 bg-gray-900/60 backdrop-blur-sm text-center">
          <p className="text-sm text-gray-400 mb-2">The debate has concluded.</p>
          <button
            onClick={handleRefresh}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm rounded-xl hover:from-purple-500 hover:to-indigo-500 transition-all"
            data-testid="debate-new-session-btn"
          >
            Start New Debate
          </button>
        </div>
      ) : (
        <form onSubmit={handleSendMessage} className="px-4 sm:px-6 py-3 border-t border-gray-700/50 bg-gray-900/60 backdrop-blur-sm">
          {/* URL attachment bar */}
          {showUrlInput && (
            <div className="flex items-center gap-2 mb-2 animate-fade-in" data-testid="url-attach-bar">
              <input
                type="url"
                value={attachedUrl}
                onChange={(e) => setAttachedUrl(e.target.value)}
                placeholder="Paste a URL (website, image, or video)..."
                className="flex-1 bg-gray-800/50 border border-purple-500/30 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
                data-testid="url-attach-input"
              />
              <button type="button" onClick={() => { setShowUrlInput(false); setAttachedUrl(''); }}
                className="text-gray-500 hover:text-gray-300 text-xs px-2 py-1">Cancel</button>
            </div>
          )}
          {attachedUrl && !showUrlInput && (
            <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-xs text-purple-300" data-testid="url-attached-badge">
              <Link2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{attachedUrl}</span>
              <button type="button" onClick={() => setAttachedUrl('')} className="text-gray-500 hover:text-white ml-auto flex-shrink-0">&times;</button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowUrlInput(!showUrlInput)}
              className={`p-2.5 rounded-xl border transition-all ${attachedUrl ? 'border-purple-500/50 text-purple-400 bg-purple-500/10' : 'border-gray-700/50 text-gray-500 hover:text-gray-300'}`}
              title="Attach a URL (website, image, or video)"
              data-testid="url-attach-btn"
            >
              <Link2 className="w-4 h-4" />
            </button>
            <input
              ref={inputRef}
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder={loading ? 'Waiting for the gods...' : isTranscribing ? 'Transcribing your voice...' : attachedUrl ? 'Ask about the attached URL...' : 'Jump into the debate...'}
              disabled={loading || responding || isTranscribing}
              className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors disabled:opacity-50"
              data-testid="living-room-input"
            />
            <button
              type="button"
              onClick={toggleChatVoice}
              disabled={loading || responding || isTranscribing}
              className={`p-2.5 rounded-xl border transition-all ${isRecordingChat
                ? 'bg-red-500/20 border-red-500/50 text-red-400 animate-pulse'
                : isTranscribing
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                  : 'border-gray-700/50 text-gray-500 hover:text-white hover:border-purple-500/30'} disabled:opacity-30`}
              title={isRecordingChat ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Speak to steer the debate'}
              data-testid="chat-mic-btn"
            >
              {isTranscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : isRecordingChat ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <button
              type="submit"
              disabled={!userInput.trim() || loading || responding || isTranscribing}
              className="p-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white disabled:opacity-30 hover:from-purple-500 hover:to-indigo-500 transition-all shadow-lg shadow-purple-500/20 disabled:shadow-none"
              data-testid="living-room-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      )}

      {/* Feature #8: Instant Replay Modal — with Audio TTS + Visualization */}
      {replayNarration && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="replay-modal">
          <div className="bg-gray-900 border border-orange-500/30 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <Play className="w-5 h-5 text-orange-400" />
              <h3 className="text-lg font-bold text-white">Instant Replay</h3>
              <button onClick={() => { setReplayNarration(null); setReplayAudio(null); setReplayPlaying(null); }} className="ml-auto p-1 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white" data-testid="replay-close-btn"><X className="w-4 h-4" /></button>
            </div>
            
            {/* Debate Flow Visualization — who allied with whom */}
            {messages.length > 0 && (
              <div className="mb-4 p-3 bg-gray-800/40 border border-gray-700/20 rounded-xl" data-testid="replay-debate-flow">
                <p className="text-[9px] text-gray-600 uppercase tracking-wider font-semibold mb-2">Debate Flow</p>
                <div className="flex items-center justify-between gap-1">
                  {messages.filter(m => m.agent !== 'user' && !m.is_entrance).slice(0, 8).map((msg, i) => {
                    const gc = GOD_CONFIG[msg.agent] || {};
                    const conf = msg.confidence || 75;
                    const mood = msg.mood || 'neutral';
                    const moodColor = mood === 'aggressive' ? 'border-red-500' : mood === 'supportive' ? 'border-green-500' : mood === 'thoughtful' ? 'border-blue-500' : 'border-gray-600';
                    return (
                      <div key={i} className="flex flex-col items-center gap-0.5 flex-1">
                        <div className={`w-8 h-8 rounded-full border-2 ${moodColor} flex items-center justify-center text-[10px] ${gc.bg || 'bg-gray-800'}`}>
                          {(gc.name || msg.agent || '?')[0]}
                        </div>
                        {/* Confidence bar as waveform segment */}
                        <div className="w-full h-8 flex items-end justify-center">
                          <div
                            className={`w-2 rounded-t transition-all ${
                              conf > 80 ? 'bg-orange-500' : conf > 60 ? 'bg-amber-500' : 'bg-gray-600'
                            }`}
                            style={{ height: `${Math.max(20, conf)}%` }}
                          />
                        </div>
                        <span className="text-[8px] text-gray-600">{conf}%</span>
                        {/* Alliance lines */}
                        {i > 0 && i < messages.filter(m => m.agent !== 'user' && !m.is_entrance).length - 1 && (
                          <div className={`w-full h-px ${
                            mood === messages.filter(m => m.agent !== 'user' && !m.is_entrance)[i-1]?.mood ? 'bg-green-500/40' : 'bg-red-500/20'
                          }`} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Breakthrough indicator */}
                {breakthroughMoment && (
                  <div className="mt-2 flex items-center gap-1.5 text-[9px] text-amber-400">
                    <Sparkles className="w-3 h-3" />
                    <span>Breakthrough moment detected during debate</span>
                  </div>
                )}
              </div>
            )}

            {/* Audio narration player */}
            {replayAudio?.narration && (
              <div className="mb-4 p-3 bg-orange-900/20 border border-orange-500/20 rounded-xl" data-testid="replay-audio-player">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => playReplayAudio(replayAudio.narration, 'narration')}
                    className={`p-2.5 rounded-full transition-all ${replayPlaying === 'narration' ? 'bg-orange-500 text-white animate-pulse' : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/40'}`}
                    data-testid="replay-play-narration"
                  >
                    {replayPlaying === 'narration' ? <Volume2 className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-orange-300">Full Narration</p>
                    <p className="text-xs text-gray-500">Dramatic replay of the entire debate</p>
                    {/* Animated waveform visualization */}
                    {replayPlaying === 'narration' && (
                      <div className="flex items-end gap-0.5 h-4 mt-1" data-testid="replay-waveform">
                        {Array.from({ length: 20 }, (_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-orange-400 rounded-full"
                            style={{
                              height: `${Math.random() * 100}%`,
                              animation: `pulse 0.${3 + (i % 5)}s ease-in-out infinite alternate`,
                              animationDelay: `${i * 0.05}s`,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Per-god audio segments with waveform */}
            {replayAudio?.segments?.length > 0 && (
              <div className="mb-4 space-y-2" data-testid="replay-god-segments">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">God Soundbites</p>
                {replayAudio.segments.map((seg, i) => {
                  const gc = GOD_CONFIG[seg.god_id] || {};
                  return (
                    <div key={i} className="flex items-center gap-3 p-2 bg-gray-800/50 rounded-lg border border-gray-700/30" data-testid={`replay-segment-${i}`}>
                      <button
                        onClick={() => playReplayAudio(seg.audio_base64, i)}
                        className={`p-1.5 rounded-full transition-all flex-shrink-0 ${replayPlaying === i ? 'bg-orange-500 text-white animate-pulse' : 'bg-gray-700 text-gray-400 hover:bg-orange-500/30 hover:text-orange-400'}`}
                      >
                        {replayPlaying === i ? <Volume2 className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs font-bold ${gc.text || 'text-gray-300'}`}>{seg.god_name}</span>
                          {/* Waveform for playing god */}
                          {replayPlaying === i && (
                            <div className="flex items-end gap-px h-3">
                              {Array.from({ length: 8 }, (_, j) => (
                                <div key={j} className={`w-0.5 rounded-full ${gc.text?.replace('text-', 'bg-') || 'bg-gray-400'}`}
                                  style={{ height: `${20 + Math.random() * 80}%`, animation: `pulse 0.${2 + j}s ease-in-out infinite alternate` }} />
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 truncate">{seg.text?.slice(0, 80)}...</p>
                      </div>
                      <span className="text-[9px] text-gray-600 uppercase flex-shrink-0">{seg.voice}</span>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Narration script */}
            <div className="bg-gradient-to-b from-orange-900/20 to-gray-900/20 rounded-xl p-4 border border-orange-500/10">
              <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">{replayNarration}</p>
            </div>
          </div>
        </div>
      )}

      {/* Feature #18: Oracle Prediction Modal */}
      {oraclePrediction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="oracle-modal">
          <div className="bg-gray-900 border border-violet-500/30 rounded-2xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-violet-400" />
              <h3 className="text-lg font-bold text-white">The Oracle Speaks</h3>
              <button onClick={() => setOraclePrediction(null)} className="ml-auto p-1 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="bg-gradient-to-b from-violet-900/20 to-gray-900/20 rounded-xl p-4 border border-violet-500/10">
              <pre className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap font-sans">{oraclePrediction}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Feature #9: Split Decision Modal */}
      {showSplitModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="split-modal">
          <div className="bg-gray-900 border border-green-500/30 rounded-2xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Swords className="w-5 h-5 text-green-400" />
              <h3 className="text-lg font-bold text-white">Split Decision</h3>
              <button onClick={() => { setShowSplitModal(false); setSplitResult(null); }} className="ml-auto p-1 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {!splitResult ? (
              <>
                <p className="text-gray-400 text-sm mb-3">Fork this debate into two parallel paths. What's the pivotal question?</p>
                <input
                  type="text" value={splitQuestion} onChange={e => setSplitQuestion(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSplitDecision(); }}
                  placeholder="e.g., What if we had double the budget?"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 mb-3 focus:outline-none focus:border-green-500/50"
                  data-testid="split-question-input"
                />
                <button onClick={handleSplitDecision} disabled={!splitQuestion.trim() || splitLoading}
                  className="w-full py-2 bg-green-600 hover:bg-green-500 disabled:opacity-40 text-white font-semibold text-sm rounded-lg transition-all flex items-center justify-center gap-2"
                  data-testid="split-execute-btn"
                >
                  {splitLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Gods are debating...</> : 'Fork the Debate'}
                </button>
              </>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                <p className="text-green-400 text-sm font-semibold">Fork: "{splitResult.fork_question}"</p>
                {['path_a', 'path_b'].map(pathKey => {
                  const path = splitResult[pathKey];
                  const chosenPath = pathKey === 'path_a' ? 'A' : 'B';
                  return (
                    <div key={pathKey} className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-white">{path.label}</p>
                        <button
                          onClick={() => handleContinueFromFork(chosenPath, splitResult.fork_question, path.messages)}
                          disabled={splitContinueLoading}
                          className="px-3 py-1 text-[10px] font-bold rounded-full bg-green-600/20 border border-green-500/40 text-green-400 hover:bg-green-600/40 hover:text-green-300 transition-all disabled:opacity-40"
                          data-testid={`split-continue-${pathKey}`}
                        >
                          {splitContinueLoading ? 'Loading...' : `Continue Path ${chosenPath}`}
                        </button>
                      </div>
                      {path.messages.map((msg, i) => {
                        const gc = GOD_CONFIG[msg.agent] || {};
                        return (
                          <div key={i} className="mb-2">
                            <span className={`text-xs font-semibold ${gc.text || 'text-gray-300'}`}>{gc.name || msg.agent}:</span>
                            <p className="text-xs text-gray-400 mt-0.5">{msg.content?.slice(0, 200)}...</p>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feature #16: Footnotes Panel — Structured Inline */}
      {footnotes && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="footnotes-modal">
          <div className="bg-gray-900 border border-cyan-500/30 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-5 h-5 text-cyan-400" />
              <h3 className="text-lg font-bold text-white">God Footnotes</h3>
              <button onClick={() => setFootnotes(null)} className="ml-auto p-1 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {(Array.isArray(footnotes) ? footnotes : []).map((fn, i) => (
                <div key={i} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/30" data-testid={`footnote-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                      fn.status === 'VERIFIED' ? 'bg-green-500/15 text-green-400 border border-green-500/30' :
                      fn.status === 'QUESTIONABLE' ? 'bg-red-500/15 text-red-400 border border-red-500/30' :
                      'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30'
                    }`}>{fn.status}</span>
                    <span className="text-[10px] text-gray-500">{fn.god}</span>
                  </div>
                  <p className="text-xs text-gray-300 italic mb-1">"{fn.claim}"</p>
                  <p className="text-[11px] text-gray-500">{fn.source}</p>
                </div>
              ))}
              {(!Array.isArray(footnotes) || footnotes.length === 0) && (
                <p className="text-gray-500 text-sm text-center">No footnotes generated.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feature #17: Pace Suggestion */}
      {debatePace && debatePace.suggestion && !showTopicSelection && !loading && (
        <div className="px-4 py-2 bg-indigo-900/10 border-t border-indigo-500/10" data-testid="pace-suggestion">
          <p className="text-xs text-indigo-300/70 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            <span className="font-medium">{debatePace.phase_label}:</span> {debatePace.suggestion}
          </p>
        </div>
      )}

      {/* Feature #2: Whisper Sidebar */}
      {whisperTarget && (
        <div className="fixed inset-y-0 right-0 w-80 bg-gray-900/98 backdrop-blur-xl border-l border-purple-500/30 shadow-2xl z-50 flex flex-col animate-slide-in-right" data-testid="whisper-panel">
          <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
            <div className="flex items-center gap-2">
              <GodAvatar config={GOD_CONFIG[whisperTarget] || GOD_CONFIG.calliope} size="w-8 h-8" />
              <div>
                <p className={`text-sm font-bold ${(GOD_CONFIG[whisperTarget] || {}).text || 'text-white'}`}>
                  {(GOD_CONFIG[whisperTarget] || {}).name || whisperTarget}
                </p>
                <p className="text-[10px] text-purple-400/60 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                  Private Channel — Off the record
                </p>
              </div>
            </div>
            <button onClick={() => { setWhisperTarget(null); setWhisperHistory([]); }} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors" data-testid="close-whisper">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <p className="text-[11px] text-purple-400/40 italic text-center mb-2">
              {(GOD_CONFIG[whisperTarget] || {}).name} is listening privately. They'll be candid here in ways they wouldn't dare in the open forum.
            </p>
            {whisperHistory.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user' 
                    ? 'bg-purple-600/20 border border-purple-500/30 text-gray-200 rounded-br-sm' 
                    : `${(GOD_CONFIG[whisperTarget] || {}).bg || 'bg-gray-800/50'} border ${(GOD_CONFIG[whisperTarget] || {}).border || 'border-gray-700'} text-gray-200 rounded-bl-sm`
                }`} data-testid={`whisper-msg-${i}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {whisperLoading && (
              <div className="flex justify-start">
                <div className={`px-4 py-3 rounded-2xl ${(GOD_CONFIG[whisperTarget] || {}).bg || 'bg-gray-800/50'} border ${(GOD_CONFIG[whisperTarget] || {}).border || 'border-gray-700'} rounded-bl-sm`}>
                  <div className="flex gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{animationDelay:'0ms'}} />
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{animationDelay:'150ms'}} />
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{animationDelay:'300ms'}} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-3 border-t border-gray-700/50 bg-gray-900/80">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={whisperMessage}
                onChange={(e) => setWhisperMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleWhisper(whisperTarget); }}}
                placeholder={`Whisper to ${(GOD_CONFIG[whisperTarget] || {}).name}...`}
                className="flex-1 bg-gray-800/80 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-all"
                data-testid="whisper-input"
                autoFocus
              />
              <button
                onClick={() => handleWhisper(whisperTarget)}
                disabled={!whisperMessage.trim() || whisperLoading}
                className="p-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white transition-all"
                data-testid="whisper-send"
              >
                {whisperLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* ═══ GOD ATMOSPHERE OVERLAY ═══ */
        .god-atmosphere-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          transition: opacity 0.6s ease, background 0.8s ease;
        }
        .god-atmosphere-overlay::after {
          content: '';
          position: absolute;
          inset: 0;
          background: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
          opacity: 0.5;
          mix-blend-mode: overlay;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }

        /* ═══ GOD DRAMATIC ENTRANCE ═══ */
        @keyframes portrait-entrance {
          0% { opacity: 0; transform: translateX(-24px) scale(0.6); filter: blur(4px) brightness(1.8); }
          40% { opacity: 1; transform: translateX(4px) scale(1.1); filter: blur(0) brightness(1.3); }
          60% { transform: translateX(-2px) scale(1.02); filter: brightness(1.1); }
          100% { opacity: 1; transform: translateX(0) scale(1); filter: blur(0) brightness(1); }
        }
        .animate-portrait-entrance {
          animation: portrait-entrance 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        @keyframes message-reveal {
          0% { opacity: 0; transform: translateY(12px) translateX(-8px); filter: blur(2px); }
          100% { opacity: 1; transform: translateY(0) translateX(0); filter: blur(0); }
        }
        .animate-message-reveal {
          animation: message-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.25s forwards;
          opacity: 0;
        }

        .god-entrance-wrapper {
          position: relative;
          padding: 2px 0;
          z-index: 1;
        }

        /* Glow trail behind portrait on entrance */
        .god-entrance-wrapper .animate-portrait-entrance::before {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 9999px;
          background: radial-gradient(circle, var(--god-glow, rgba(147,51,234,0.3)) 0%, transparent 70%);
          animation: glow-trail 1s ease-out forwards;
          pointer-events: none;
          z-index: -1;
        }
        @keyframes glow-trail {
          0% { opacity: 0.8; transform: scale(1.5); }
          100% { opacity: 0; transform: scale(0.8); }
        }

        /* Ambient glow when god is actively speaking */
        .god-speaking {
          position: relative;
        }
        .god-speaking::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 3px;
          border-radius: 4px;
          background: linear-gradient(to bottom, transparent, var(--god-glow-strong, rgba(147,51,234,0.6)), transparent);
          animation: speaking-bar 1.5s ease-in-out infinite;
        }
        @keyframes speaking-bar {
          0%, 100% { opacity: 0.4; height: 60%; top: 20%; }
          50% { opacity: 1; height: 90%; top: 5%; }
        }

        /* Sound bar animation for the equalizer icon */
        @keyframes sound-bar-1 { 0%,100%{height:6px}50%{height:14px} }
        @keyframes sound-bar-2 { 0%,100%{height:10px}50%{height:6px} }
        @keyframes sound-bar-3 { 0%,100%{height:4px}50%{height:12px} }
        .animate-sound-bar-1 { animation: sound-bar-1 0.6s ease-in-out infinite; }
        .animate-sound-bar-2 { animation: sound-bar-2 0.6s ease-in-out infinite 0.15s; }
        .animate-sound-bar-3 { animation: sound-bar-3 0.6s ease-in-out infinite 0.3s; }

        /* Speaking pulse on avatar */
        @keyframes speaking-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        .animate-speaking-pulse {
          animation: speaking-pulse 1.2s ease-in-out infinite;
        }

        @keyframes thirteenth-entrance {
          0% { opacity: 0; transform: translateY(20px) scale(0.95); filter: brightness(2) blur(4px); }
          30% { opacity: 0.7; transform: translateY(10px) scale(0.98); filter: brightness(1.5) blur(1px); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: brightness(1) blur(0); }
        }
        .animate-thirteenth-entrance {
          animation: thirteenth-entrance 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes thirteenth-whisper {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.5; }
        }
        .animate-thirteenth-whisper {
          animation: thirteenth-whisper 4s ease-in-out infinite;
        }
        @keyframes thirteenth-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-thirteenth-fade {
          animation: thirteenth-fade 2s ease-in forwards;
        }

        /* ═══ GOD REACTIONS POP-IN ═══ */
        @keyframes reaction-pop {
          0% { opacity: 0; transform: scale(0.3) translateY(4px); }
          60% { opacity: 1; transform: scale(1.15) translateY(-1px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-reaction-pop {
          animation: reaction-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .animate-reaction-pop:nth-child(2) { animation-delay: 0.1s; opacity: 0; }
        .animate-reaction-pop:nth-child(3) { animation-delay: 0.2s; opacity: 0; }
        .animate-reaction-pop:nth-child(4) { animation-delay: 0.3s; opacity: 0; }

        /* ═══ WHISPER SLIDE-IN ═══ */
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out forwards;
        }

        /* ═══ TENSION EDGE GLOW — screen edges pulse at high tension ═══ */
        @keyframes tension-edge-glow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .animate-tension-edge-glow {
          animation: tension-edge-glow 2s ease-in-out infinite;
        }

        /* ═══ BREAKTHROUGH GOLDEN FLASH — dramatic full-screen burst ═══ */
        @keyframes breakthrough-flash {
          0% { background: rgba(251, 191, 36, 0); }
          10% { background: rgba(251, 191, 36, 0.35); }
          25% { background: rgba(251, 191, 36, 0.15); }
          40% { background: rgba(251, 191, 36, 0.25); }
          60% { background: rgba(251, 191, 36, 0.08); }
          100% { background: rgba(251, 191, 36, 0); }
        }
        .animate-breakthrough-flash {
          animation: breakthrough-flash 2.5s ease-out forwards;
        }

        /* ═══ DRAMATIC GOD ENTRANCE ═══ */
        @keyframes god-entrance {
          0% { opacity: 0; transform: scaleX(0.3); filter: brightness(3); }
          30% { opacity: 1; transform: scaleX(1.05); filter: brightness(1.8); }
          60% { transform: scaleX(0.98); filter: brightness(1.2); }
          100% { transform: scaleX(1); filter: brightness(1); }
        }
        .animate-god-entrance {
          animation: god-entrance 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
      </> /* end !showTopicSelection */}
      {/* Consciousness Milestone Modal */}
      {pendingMilestone && (
        <ConsciousnessMilestoneModal milestone={pendingMilestone} onDismiss={dismissMilestone} />
      )}

      {/* Feature #12: The Challenge — Cross-User God Battles Modal */}
      {showChallengeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="challenge-modal">
          <div className="bg-gray-900 border border-rose-500/30 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <Swords className="w-5 h-5 text-rose-400" />
              <h3 className="text-lg font-bold text-white">The Challenge Arena</h3>
              <button onClick={() => { setShowChallengeModal(false); setChallengeResult(null); }}
                className="ml-auto p-1 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white" data-testid="challenge-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>

            {challengeResult ? (
              <div className="space-y-3" data-testid="challenge-result">
                <div className="p-3 bg-rose-900/20 border border-rose-500/20 rounded-xl">
                  <p className="text-xs text-rose-400 font-bold uppercase mb-1">Original Verdict Challenged</p>
                  <p className="text-sm text-gray-300 italic">"{challengeResult.original_verdict?.slice(0, 200)}"</p>
                </div>
                <p className="text-xs text-gray-500 uppercase font-semibold">Your Gods' Counter-Arguments</p>
                {(challengeResult.counter_arguments || []).map((msg, i) => {
                  const gc = GOD_CONFIG[msg.agent] || {};
                  return (
                    <div key={i} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/30">
                      <span className={`text-xs font-bold ${gc.text || 'text-gray-300'}`}>{gc.name || msg.agent}</span>
                      <p className="text-xs text-gray-400 mt-1">{msg.content?.slice(0, 300)}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3" data-testid="challenge-feed">
                <p className="text-gray-400 text-sm">Published verdicts from other strategists. Pick one to challenge with your own gods.</p>
                {challengeFeed.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 text-sm">No open challenges yet.</p>
                    <p className="text-gray-700 text-xs mt-1">Publish your next verdict to start the first challenge!</p>
                  </div>
                ) : (
                  challengeFeed.map((ch, i) => {
                    const verdictPreview = typeof ch.verdict === 'object'
                      ? (ch.verdict?.summary || ch.verdict?.verdict || JSON.stringify(ch.verdict)).slice(0, 120)
                      : String(ch.verdict || '').slice(0, 120);
                    return (
                      <div key={i} className="bg-gray-800/50 border border-gray-700/30 rounded-xl p-3" data-testid={`challenge-${i}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-bold text-white">{ch.topic?.slice(0, 60)}</span>
                          <span className="text-[9px] text-gray-600">{ch.published_by}</span>
                        </div>
                        <p className="text-[11px] text-gray-500 italic mb-2">"{verdictPreview}..."</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] text-gray-600">{ch.challengers?.length || 0} challengers</span>
                          <button
                            onClick={() => handleAcceptChallenge(ch.challenge_id)}
                            disabled={challengeLoading}
                            className="px-3 py-1 text-[10px] font-bold rounded-full bg-rose-600/20 border border-rose-500/40 text-rose-400 hover:bg-rose-600/40 transition-all disabled:opacity-40"
                            data-testid={`accept-challenge-${i}`}
                          >
                            {challengeLoading ? 'Battling...' : 'Accept Challenge'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LivingWarRoom;
